import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams, useSearchParams, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import axios from "axios";
import DOMPurify from "dompurify";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShoppingCart, User, Menu, X, Calendar, Package, 
  Sparkles, Eye, Heart, Shield, Star, ArrowRight, ArrowLeft,
  Phone, Mail, MapPin, Facebook, Instagram, Youtube, Clock,
  ChevronLeft, ChevronRight, Minus, Plus, Trash2, LogOut, Home,
  LayoutDashboard, Settings, Users, TrendingUp,
  Store, Truck, CheckCircle, Info, CreditCard, Check, RefreshCw, Bell, Send, Compass, Car, Download, Lock, ShieldCheck, Gift
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { ScrollArea } from "./components/ui/scroll-area";
import { Separator } from "./components/ui/separator";
import { Calendar as CalendarComponent } from "./components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";
import { Label } from "./components/ui/label";
import ProductDetailPage from "./components/ProductDetailPage";
import VoyanceDistancePage from "./components/VoyanceDistancePage";
import VoyanceMontelimarPage from "./components/VoyanceMontelimarPage";
import PranicHealingMontelimarPage from "./components/PranicHealingMontelimarPage";
import NumerologieMontelimarPage from "./components/NumerologieMontelimarPage";
import NoIndexBanner from "./components/NoIndexBanner";
import TrustStrip from "./components/TrustStrip";
import { isFeatured, sortFeaturedFirst, pickFeaturedForHome } from "./lib/featured";
import { resolveServicePage } from "./legacyRedirects";
import QuiSuisJePage from "./components/QuiSuisJePage";
import NewsletterUnsubscribePage from "./components/NewsletterUnsubscribePage";
import { LegalNoticePage, PrivacyPolicyPage, CguPage, ConsultationPolicyPage } from "./components/LegalPages";
import { CookieBanner } from "./components/CookieBanner";
import { ConsultationPolicyModal } from "./components/ConsultationPolicyModal";
import { MobileBookingCTA } from "./components/MobileBookingCTA";

import { SquarePaymentForm } from "./components/SquarePaymentForm";
import PromoCodeInput from "./components/PromoCodeInput";
import MondialRelayPicker from "./components/MondialRelayPicker";
import SendcloudServicePointPicker from "./components/SendcloudServicePointPicker";
import SendcloudLabelGenerator from "./components/SendcloudLabelGenerator";
import PayPendingModal from "./components/PayPendingModal";
import RescheduleModal from "./components/RescheduleModal";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { PushSubscribeButton } from "./components/PushSubscribeButton";
import { AdminEventManager } from "./components/admin/AdminEventManager";
import { AdminBlogManager } from "./components/admin/AdminBlogManager";
import { AdminPromoManager } from "./components/admin/AdminPromoManager";
import { AdminProductsManager } from "./components/admin/AdminProductsManager";
import { AdminPushBroadcast } from "./components/admin/AdminPushBroadcast";
import { AdminMainCategoriesManager } from "./components/admin/AdminMainCategoriesManager";
import { SquareSyncButton } from "./components/admin/SquareSyncButton";
import {
  AuthChoiceModal,
  LoginPage,
  RegisterPage,
  VerifyEmailPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ConfirmEmailChangePage,
  ProfileSecuritySection,
  _setUseAuth,
} from "./components/AuthPages";
import { registerServiceWorker } from "./lib/push";
import { AuthContext, CartContext, useAuth, useCart } from "./contexts/AppContexts";
import CheckoutPage from "./pages/CheckoutPage";
import BookingPage from "./pages/BookingPage";
import DashboardPage from "./pages/DashboardPage";
import EvjfPage from "./pages/EvjfPage";
import SoireePriveePage from "./pages/SoireePriveePage";
import RetreatsPage from "./pages/RetreatsPage";
import RetreatDetailPage from "./pages/RetreatDetailPage";
import RetreatBookingSuccessPage from "./pages/RetreatBookingSuccessPage";
import RetreatLandingDromeProvence from "./pages/RetreatLandingDromeProvence";
import RetreatLandingSudFrance from "./pages/RetreatLandingSudFrance";
import PayAppointmentByLink from "./pages/PayAppointmentByLink";
import { enrichService } from "./lib/serviceEnrich";
import { stripMarkdown } from "./components/MarkdownLite";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== SEO HELPER ==============
// Composant reutilisable pour les meta tags par page (react-helmet-async).
// Inclut titre, description, mots-cles local + national, canonical, Open Graph
// et, en option, un bloc JSON-LD de donnees structurees.
import { ROBOTS_META_CONTENT } from "./lib/indexing";
const SITE_URL = process.env.REACT_APP_SITE_URL || "https://www.bilan2vie.fr";
const DEFAULT_OG_IMAGE = SITE_URL + "/og-image.png";

function Seo({ title, description, path = "/", keywords, image, jsonLd, type = "website" }) {
  const fullTitle = title ? title + " | Bilan 2 Vie Montélimar" : "Bilan 2 Vie — Voyance & Boutique Spirituelle à Montélimar";
  const url = SITE_URL + path;
  const img = image || DEFAULT_OG_IMAGE;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={ROBOTS_META_CONTENT} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="fr_FR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// ============== CONTEXT ==============

// Register useAuth in AuthPages module (loose-coupling pattern to avoid circular import)
_setUseAuth(useAuth);

// ============== AUTH PROVIDER ==============

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const checkAuth = async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    // checkAuth est volontairement appele une seule fois au montage (auth init)
  }, []);

  const _memorizeReturnTo = (returnTo) => {
    try {
      const target = returnTo || (window.location.pathname + window.location.search + window.location.hash);
      if (target && !target.startsWith('/auth') && !target.startsWith('/connexion') && !target.startsWith('/inscription') && target !== '/') {
        sessionStorage.setItem('post_login_redirect', target);
      } else {
        sessionStorage.removeItem('post_login_redirect');
      }
    } catch (_e) { /* private mode : skip */ }
  };

  const googleLogin = (returnTo) => {
    _memorizeReturnTo(returnTo);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // login() ouvre la modal de choix (Google ou Email/MDP)
  const login = (returnTo) => {
    _memorizeReturnTo(returnTo);
    setAuthModalOpen(true);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Déconnexion réussie");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, googleLogin, logout, checkAuth, authModalOpen, setAuthModalOpen }}>
      {children}
    </AuthContext.Provider>
  );
}

// ============== CART PROVIDER ==============

function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('bilan2vie_cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('bilan2vie_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, customPrice = null) => {
    const price = customPrice || product.price;
    const variationId = product.selected_variation?.variation_id;
    // CartId unique : inclut variation pour séparer les déclinaisons du même produit
    let cartId;
    if (customPrice) {
      cartId = `${product.product_id}_gc_${price}`;
    } else if (variationId) {
      cartId = `${product.product_id}_var_${variationId}`;
    } else {
      cartId = product.product_id;
    }
    setCart(prev => {
      const existing = prev.find(item => item.product_id === cartId);
      if (existing) {
        return prev.map(item =>
          item.product_id === cartId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        ...product,
        product_id: cartId,
        base_product_id: product.product_id,
        variation_id: variationId || null,
        variation_name: product.selected_variation?.name || null,
        price,
        quantity: 1,
        is_gift_card: !!customPrice
      }];
    });
    const variationLabel = product.selected_variation?.name ? ` — ${product.selected_variation.name}` : '';
    toast.success(`${product.name}${variationLabel}${customPrice ? ` (${price} €)` : ''} ajouté au panier`);
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCart(prev => prev.map(item => 
      item.product_id === productId ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => setCart([]);

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, total, itemCount }}>
      {children}
    </CartContext.Provider>
  );
}

// ============== AUTH CALLBACK ==============

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, 
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          if (response.data.success) {
            setUser(response.data.user);
            toast.success("Connexion réussie !");
            // Si l'utilisateur etait au milieu d'un tunnel (booking, checkout, event...),
            // on le renvoie sur l'ecran d'origine plutot que sur /dashboard.
            let redirectTo = '/dashboard';
            try {
              const saved = sessionStorage.getItem('post_login_redirect');
              if (saved) {
                redirectTo = saved;
                sessionStorage.removeItem('post_login_redirect');
              }
            } catch (_e) { /* private mode */ }
            navigate(redirectTo, { replace: true, state: { user: response.data.user } });
          }
        } catch (error) {
          console.error("Auth error:", error);
          toast.error("Erreur d'authentification");
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1B132B]">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#D926A9] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#E2D5F8]">Connexion en cours...</p>
      </div>
    </div>
  );
}

// ============== HEADER ==============

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const { cart, itemCount, total, removeFromCart, updateQuantity } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // Ferme automatiquement le menu mobile dès qu'on change de page.
  // Filet de sécurité au cas où un Link/Bouton oublie setIsOpen(false).
  useEffect(() => {
    setIsOpen(false);
    setIsCartOpen(false);
  }, [location.pathname]);

  const goToCheckout = () => {
    setIsCartOpen(false);
    setIsOpen(false);
    navigate('/checkout');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 nav-glass">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_spirituel-wellness/artifacts/ouy7p36h_Logo%20Bilan%202%20Vie%20simple%20transp%281600%20x%201000%20px%29.png" 
            alt="Bilan 2 Vie" 
            className="h-14 logo-lotus"
            data-testid="logo"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-[#E2D5F8] hover:text-white transition-colors" data-testid="nav-home">Accueil</Link>
          <Link to="/qui-suis-je" className="text-[#E2D5F8] hover:text-white transition-colors" data-testid="nav-about">À propos</Link>
          <Link to="/services" className="text-[#E2D5F8] hover:text-white transition-colors" data-testid="nav-services">Services</Link>
          <Link to="/evenements" className="text-[#E2D5F8] hover:text-white transition-colors" data-testid="nav-events">Événements</Link>
          <Link to="/retraites" className="text-[#E2D5F8] hover:text-white transition-colors" data-testid="nav-retreats">Séjours & Retraites</Link>
          <Link to="/blog" className="text-[#E2D5F8] hover:text-white transition-colors" data-testid="nav-blog">Blog</Link>
          <Link to="/boutique" className="text-[#E2D5F8] hover:text-white transition-colors" data-testid="nav-boutique">Boutique</Link>
          <Link to="/booking" className="text-[#E2D5F8] hover:text-white transition-colors" data-testid="nav-booking">Réserver</Link>
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-4">
          {/* Cart */}
          <Dialog open={isCartOpen} onOpenChange={setIsCartOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="cart-button">
                <ShoppingCart className="w-5 h-5 text-[#00E5FF]" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D926A9] rounded-full text-xs flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#261B3B] border-white/10 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Ton Panier</DialogTitle>
              </DialogHeader>
              {cart.length === 0 ? (
                <p className="text-[#B59ED1] text-center py-8">Ton panier est vide</p>
              ) : (
                <>
                  <ScrollArea className="max-h-[400px]">
                    {cart.map(item => (
                      <div key={item.product_id} className="flex items-center gap-4 py-4 border-b border-white/10" data-testid={`cart-item-${item.product_id}`}>
                        <img src={item.image_url || '/api/static/images/fallback_product.jpg'} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate">{item.name}</h4>
                          {item.variation_name && (
                            <p className="text-[11px] text-[#B59ED1] italic mt-0.5" data-testid={`cart-item-variation-${item.product_id}`}>
                              Variante : {item.variation_name}
                            </p>
                          )}
                          <p className="text-[#D926A9] font-semibold mt-1">{item.price?.toFixed(2)} €</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-red-500" onClick={() => removeFromCart(item.product_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </ScrollArea>
                  <Separator className="bg-white/10" />
                  <div className="flex justify-between items-center py-4">
                    <span className="text-lg">Total</span>
                    <span className="text-2xl font-serif text-[#D926A9]">{total.toFixed(2)} €</span>
                  </div>
                  <div className="rounded-lg bg-[#1B132B] border border-white/10 p-3 space-y-1.5 text-xs text-[#B59ED1] mb-3" data-testid="cart-shipping-note">
                    <div className="flex items-center gap-2">
                      <Truck className="w-3.5 h-3.5 text-[#00E5FF] shrink-0" />
                      <span>Point relais dès 3,99 € · <span className="text-[#E2D5F8]">retrait gratuit en boutique</span></span>
                    </div>
                    {total >= 80 ? (
                      <div className="flex items-center gap-2 text-emerald-400">
                        <Gift className="w-3.5 h-3.5 shrink-0" /><span className="font-medium">Livraison offerte !</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Gift className="w-3.5 h-3.5 text-[#D926A9] shrink-0" /><span>Plus que <span className="text-[#E2D5F8] font-medium">{(80 - total).toFixed(2)} €</span> pour la livraison offerte</span>
                      </div>
                    )}
                  </div>
                  <Button onClick={goToCheckout} className="w-full gradient-primary" data-testid="checkout-button">
                    Commander
                  </Button>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* User */}
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" className="flex items-center gap-2" data-testid="dashboard-link">
                  <User className="w-5 h-5 text-[#D926A9]" />
                  <span className="text-[#E2D5F8]">{user.name?.split(' ')[0]}</span>
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
                <LogOut className="w-5 h-5 text-[#B59ED1]" />
              </Button>
            </div>
          ) : (
            <Button onClick={login} className="hidden md:flex gradient-primary" data-testid="login-button">
              <User className="w-4 h-4 mr-2" />
              Connexion
            </Button>
          )}

          {/* Mobile Menu */}
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#261B3B] border-t border-white/10"
          >
            <nav className="flex flex-col p-6 gap-4">
              <Link to="/" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">Accueil</Link>
              <Link to="/qui-suis-je" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">À propos</Link>
              <Link to="/services" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">Services</Link>
              <Link to="/evenements" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">Événements</Link>
              <Link to="/retraites" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">Séjours & Retraites</Link>
              <Link to="/blog" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">Blog</Link>
              <Link to="/boutique" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">Boutique</Link>
              <Link to="/booking" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">Réserver</Link>
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsOpen(false)} className="text-[#E2D5F8] hover:text-white py-2">Mon Espace</Link>
                  <Button onClick={() => { logout(); setIsOpen(false); }} variant="ghost" className="justify-start text-[#B59ED1]">
                    <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                  </Button>
                </>
              ) : (
                <Button onClick={() => { setIsOpen(false); login(); }} className="gradient-primary mt-4">
                  <User className="w-4 h-4 mr-2" /> Connexion
                </Button>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ============== FOOTER ==============

const DAY_LABELS = { 0: 'Lundi', 1: 'Mardi', 2: 'Mercredi', 3: 'Jeudi', 4: 'Vendredi', 5: 'Samedi', 6: 'Dimanche' };
const DAY_SHORT = { 0: 'Lun', 1: 'Mar', 2: 'Mer', 3: 'Jeu', 4: 'Ven', 5: 'Sam', 6: 'Dim' };

// Section divider with a glowing gradient line + central icon + uppercase label.
// Same design as the events page sub-sections (Soirées Mystiques, Ateliers).
function SectionDivider({ icon, label, color = '#D926A9' }) {
  const Icon = icon || Sparkles;
  return (
    <div className="flex items-center justify-center w-full mb-6 opacity-95">
      <div className="h-px w-24 sm:w-32" style={{ background: `linear-gradient(to right, transparent, ${color}90)` }} />
      <div className="mx-4 sm:mx-6 flex flex-col items-center gap-2">
        <Icon className="w-5 h-5" style={{ color, filter: `drop-shadow(0 0 8px ${color}80)` }} />
        <span className="text-xs uppercase tracking-[0.3em] text-[#E2D5F8] font-light whitespace-nowrap">{label}</span>
      </div>
      <div className="h-px w-24 sm:w-32" style={{ background: `linear-gradient(to left, transparent, ${color}90)` }} />
    </div>
  );
}

/**
 * Decorative atmosphere overlay for sections.
 * Variants: 'aurora-cyan' | 'aurora-magenta' | 'aurora-gold' | 'portal' | 'surface'
 */
function SectionAtmosphere({ variant = 'aurora-cyan', stars = true }) {
  return (
    <>
      {/* Bordure top lumineuse */}
      {variant === 'aurora-cyan' && <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00E5FF]/30 to-transparent pointer-events-none" />}
      {variant === 'aurora-magenta' && <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D926A9]/30 to-transparent pointer-events-none" />}
      {variant === 'aurora-gold' && <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FFD700]/25 to-transparent pointer-events-none" />}
      {variant === 'surface' && <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#9D4CDD]/35 to-transparent pointer-events-none" />}

      {/* Halos */}
      {variant === 'aurora-cyan' && <div className="absolute -top-[20%] right-0 w-[600px] h-[600px] halo-corner-tr blur-3xl pointer-events-none" />}
      {variant === 'aurora-magenta' && <div className="absolute -top-[15%] right-0 w-[600px] h-[600px] halo-corner-tr-magenta blur-3xl pointer-events-none" />}
      {variant === 'aurora-gold' && (
        <>
          <div className="absolute -top-[15%] right-[12%] w-[500px] h-[500px] halo-corner-tr blur-3xl pointer-events-none" />
          <div className="absolute -bottom-[15%] left-[8%] w-[500px] h-[500px] halo-corner-bl blur-3xl pointer-events-none" />
        </>
      )}
      {variant === 'portal' && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85vw] h-[85vw] max-w-[1100px] max-h-[1100px] halo-center-portal blur-3xl pointer-events-none" />}
      {variant === 'surface' && <div className="absolute -bottom-[10%] left-0 w-[600px] h-[600px] halo-corner-bl blur-3xl pointer-events-none" />}

      {/* Texture noise */}
      <div className="absolute inset-0 bg-noise pointer-events-none" />

      {/* Étoiles décoratives */}
      {stars && (
        <>
          <span className="deco-star cyan" style={{ top: '12%', left: '7%' }} />
          <span className="deco-star" style={{ top: '28%', left: '4%' }} />
          <span className="deco-star" style={{ top: '45%', left: '12%' }} />
          <span className="deco-star magenta" style={{ top: '62%', right: '9%' }} />
          <span className="deco-star" style={{ top: '78%', right: '16%' }} />
          <span className="deco-star cyan" style={{ top: '18%', right: '22%' }} />
          <span className="deco-star" style={{ top: '55%', right: '32%' }} />
          <span className="deco-star magenta" style={{ bottom: '15%', left: '20%' }} />
        </>
      )}
    </>
  );
}

function formatHour(time) {
  if (!time) return '';
  const [h, m] = time.split(':');
  return m === '00' ? `${parseInt(h)}h` : `${parseInt(h)}h${m}`;
}

// Formate une Date JS en YYYY-MM-DD en heure LOCALE (évite le décalage UTC qui
// shifte d'un jour quand on est en Europe/Paris et qu'on sélectionne une date à minuit local).
function formatLocalDate(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function useShopHours() {
  const [shopHours, setShopHours] = useState(null);
  useEffect(() => {
    axios.get(`${API}/availability/settings`).then(res => {
      setShopHours(res.data.shop_hours || {});
    }).catch(() => {});
  }, []);
  return shopHours;
}

function ShopHoursDisplay({ shopHours, compact = false }) {
  if (!shopHours) return null;
  const entries = Object.entries(shopHours).sort(([a], [b]) => Number(a) - Number(b));
  if (compact) {
    return <span className="text-[#B59ED1]">{entries.map(([d, h]) => `${DAY_SHORT[d]}: ${formatHour(h.open)}-${formatHour(h.close)}`).join(' • ')}</span>;
  }
  return (
    <ul className="space-y-1.5">
      {entries.map(([d, h]) => (
        <li key={d} className="flex items-baseline gap-2 text-sm leading-snug">
          <span className="text-[#B59ED1] min-w-[68px]">{DAY_LABELS[d]}</span>
          <span className="text-[#E2D5F8] tabular-nums whitespace-nowrap">{formatHour(h.open)} – {formatHour(h.close)}</span>
        </li>
      ))}
    </ul>
  );
}

// Résumé court des jours d'ouverture (ex. "du mardi au samedi") pour les bandeaux.
function shopDaysSummary(shopHours) {
  const days = Object.keys(shopHours || {}).map(Number).sort((a, b) => a - b);
  if (days.length === 0) return '';
  if (days.length === 1) return `le ${DAY_LABELS[days[0]].toLowerCase()}`;
  const contiguous = days.every((d, i) => i === 0 || d === days[i - 1] + 1);
  if (contiguous) return `du ${DAY_LABELS[days[0]].toLowerCase()} au ${DAY_LABELS[days[days.length - 1]].toLowerCase()}`;
  return days.map(d => DAY_LABELS[d].toLowerCase()).join(', ');
}

function Footer() {
  const shopHours = useShopHours();

  // -----------------------------------------------------------
  // JSON-LD : LocalBusiness + Store global — présent sur toutes les pages
  // Améliore le SEO local (Knowledge Graph Google) et la fiche entreprise.
  // -----------------------------------------------------------
  const DAYS_SCHEMA = [
    "https://schema.org/Monday",
    "https://schema.org/Tuesday",
    "https://schema.org/Wednesday",
    "https://schema.org/Thursday",
    "https://schema.org/Friday",
    "https://schema.org/Saturday",
    "https://schema.org/Sunday",
  ];
  const openingHoursSpec = shopHours
    ? Object.entries(shopHours)
        .filter(([, h]) => h && h.open && h.close)
        .map(([d, h]) => ({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: DAYS_SCHEMA[Number(d)],
          opens: h.open,
          closes: h.close,
        }))
    : [];
  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "Store"],
    "@id": SITE_URL + "/#localbusiness",
    name: "Bilan 2 Vie",
    legalName: "Bilan 2 Vie",
    description:
      "Voyance, soin énergétique Pranic Healing, numérologie et boutique spirituelle à Montélimar (Drôme). Consultations sur place ou à distance partout en France.",
    url: SITE_URL,
    image: SITE_URL + "/logo.png",
    logo: SITE_URL + "/logo.png",
    telephone: "+33768489056",
    email: "guillaume@bilan2vie.fr",
    priceRange: "€€",
    paymentAccepted: "Carte bancaire, Espèces, Apple Pay, Google Pay",
    currenciesAccepted: "EUR",
    address: {
      "@type": "PostalAddress",
      streetAddress: "20 quai du Roubion",
      addressLocality: "Montélimar",
      postalCode: "26200",
      addressRegion: "Drôme",
      addressCountry: "FR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 44.5586,
      longitude: 4.7506,
    },
    hasMap: "https://share.google/gsMAN7kdqzvUWaIbJ",
    openingHoursSpecification: openingHoursSpec,
    areaServed: [
      { "@type": "City", name: "Montélimar" },
      { "@type": "City", name: "Valence" },
      { "@type": "City", name: "Avignon" },
      { "@type": "City", name: "Nyons" },
      { "@type": "AdministrativeArea", name: "Drôme" },
      { "@type": "AdministrativeArea", name: "Ardèche" },
      { "@type": "AdministrativeArea", name: "Auvergne-Rhône-Alpes" },
      { "@type": "Country", name: "France" },
    ],
    sameAs: [
      "https://www.facebook.com/Bilan2Vie/",
      "https://www.instagram.com/bilan2vie/",
      "https://www.youtube.com/@Bilan2Vie/",
      "https://www.tiktok.com/@bilan2vie",
      "https://share.google/gsMAN7kdqzvUWaIbJ",
    ],
  };

  return (
    <footer className="footer py-16 pb-32 md:pb-16" aria-label="Pied de page Bilan 2 Vie">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd)}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_spirituel-wellness/artifacts/3bonmlu6_logo%20bilan2vie%202.png"
              alt="Logo Bilan 2 Vie — Voyance, Pranic Healing et boutique spirituelle à Montélimar"
              className="h-24 mb-6"
            />
            <p className="text-[#B59ED1] leading-relaxed mb-3">
              Ta boutique spirituelle à <strong className="text-[#E2D5F8]">Montélimar (Drôme)</strong>. Voyance, soin énergétique Pranic Healing et articles ésotériques pour accompagner ton chemin de vie.
            </p>
            <p className="text-[#B59ED1] text-sm leading-relaxed mb-6">
              ✨ <span className="text-[#00E5FF]">Consultations à distance partout en France</span> (téléphone, visio, email).
            </p>
            <div className="flex gap-3 flex-wrap">
              <a href="https://www.facebook.com/Bilan2Vie/" target="_blank" rel="noopener noreferrer me" aria-label="Facebook Bilan 2 Vie" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#D926A9]/20 transition-colors" data-testid="social-facebook">
                <Facebook className="w-5 h-5 text-[#00E5FF]" />
              </a>
              <a href="https://www.instagram.com/bilan2vie/" target="_blank" rel="noopener noreferrer me" aria-label="Instagram Bilan 2 Vie" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#D926A9]/20 transition-colors" data-testid="social-instagram">
                <Instagram className="w-5 h-5 text-[#00E5FF]" />
              </a>
              <a href="https://www.youtube.com/@Bilan2Vie/" target="_blank" rel="noopener noreferrer me" aria-label="YouTube Bilan 2 Vie" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#D926A9]/20 transition-colors" data-testid="social-youtube">
                <Youtube className="w-5 h-5 text-[#00E5FF]" />
              </a>
              <a href="https://www.tiktok.com/@bilan2vie" target="_blank" rel="noopener noreferrer me" aria-label="TikTok Bilan 2 Vie" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#D926A9]/20 transition-colors" data-testid="social-tiktok">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#00E5FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-serif text-xl mb-6">Navigation</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-[#B59ED1] hover:text-[#00E5FF]">Accueil</Link></li>
              <li><Link to="/qui-suis-je" className="text-[#B59ED1] hover:text-[#00E5FF]">À propos</Link></li>
              <li><Link to="/services" className="text-[#B59ED1] hover:text-[#00E5FF]">Services</Link></li>
              <li><Link to="/voyance-a-distance" className="text-[#B59ED1] hover:text-[#00E5FF]">Voyance à distance</Link></li>
              <li><Link to="/evenements" className="text-[#B59ED1] hover:text-[#00E5FF]">Événements</Link></li>
              <li><Link to="/retraites" className="text-[#B59ED1] hover:text-[#00E5FF]">Séjours & Retraites</Link></li>
              <li><Link to="/blog" className="text-[#B59ED1] hover:text-[#00E5FF]">Blog</Link></li>
              <li><Link to="/boutique" className="text-[#B59ED1] hover:text-[#00E5FF]">Boutique</Link></li>
              <li><Link to="/booking" className="text-[#B59ED1] hover:text-[#00E5FF]">Réserver</Link></li>
            </ul>
          </div>

          {/* Voyance & Soins (SEO pillars) */}
          <div>
            <h4 className="font-serif text-xl mb-6">Voyance & Soins</h4>
            <ul className="space-y-3">
              <li><Link to="/voyance-montelimar" className="text-[#B59ED1] hover:text-[#00E5FF]">Voyance à Montélimar</Link></li>
              <li><Link to="/pranic-healing-montelimar" className="text-[#B59ED1] hover:text-[#00E5FF]">Pranic Healing Montélimar</Link></li>
              <li><Link to="/numerologie-montelimar" className="text-[#B59ED1] hover:text-[#00E5FF]">Numérologie Montélimar</Link></li>
              <li><Link to="/medium-montelimar" className="text-[#B59ED1] hover:text-[#00E5FF]">Médium à Montélimar</Link></li>
              <li><Link to="/voyance-a-distance" className="text-[#B59ED1] hover:text-[#00E5FF]">Voyance par téléphone & visio</Link></li>
              <li className="pt-2">
                <a
                  href="https://share.google/gsMAN7kdqzvUWaIbJ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[#00E5FF] hover:text-white text-sm"
                  data-testid="footer-google-business"
                  aria-label="Voir notre fiche Google et avis clients"
                >
                  ⭐ Avis Google
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-xl mb-6">Contact</h4>
            <address className="not-italic">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#D926A9] mt-0.5 flex-shrink-0" />
                  <a
                    href="https://share.google/gsMAN7kdqzvUWaIbJ"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#B59ED1] hover:text-[#00E5FF]"
                    data-testid="footer-address"
                  >
                    20 quai du Roubion<br />26200 Montélimar<br /><span className="text-xs text-[#B59ED1]/80">Drôme · Auvergne-Rhône-Alpes</span>
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#D926A9] mt-0.5 flex-shrink-0" />
                  <a href="tel:+33768489056" className="text-[#B59ED1] hover:text-[#00E5FF]" data-testid="footer-phone">+33 7 68 48 90 56</a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#D926A9] mt-0.5 flex-shrink-0" />
                  <a href="mailto:guillaume@bilan2vie.fr" className="text-[#B59ED1] hover:text-[#00E5FF] break-all">guillaume@bilan2vie.fr</a>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#D926A9] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-[#D926A9]/80 mb-1.5">Horaires d'ouverture</p>
                    {shopHours ? <ShopHoursDisplay shopHours={shopHours} /> : <span className="text-[#B59ED1] text-sm">Chargement...</span>}
                  </div>
                </li>
              </ul>
            </address>
          </div>
        </div>

        <Separator className="bg-white/10 my-12" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#B59ED1] text-sm">© {new Date().getFullYear()} Bilan 2 Vie — Voyance & soin énergétique à Montélimar. Tous droits réservés.</p>
          <div className="flex flex-wrap gap-4 md:gap-6 justify-center md:justify-end">
            <Link to="/mentions-legales" className="text-[#B59ED1] text-sm hover:text-white" data-testid="footer-legal">Mentions légales</Link>
            <Link to="/politique-de-confidentialite" className="text-[#B59ED1] text-sm hover:text-white" data-testid="footer-privacy">Politique de confidentialité</Link>
            <Link to="/cgv" className="text-[#B59ED1] text-sm hover:text-white" data-testid="footer-cgv">CGV</Link>
            <Link to="/politique-consultations" className="text-[#B59ED1] text-sm hover:text-white" data-testid="footer-consultation-policy">Politique consultations</Link>
          </div>
        </div>

        <div className="mt-6 text-center md:text-right">
          <p className="text-[#B59ED1]/70 text-xs">
            Conception & Design :{" "}
            <a
              href="https://monbeausite.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#B59ED1] hover:text-[#00E5FF] transition-colors underline-offset-2 hover:underline"
              data-testid="footer-credit-monbeausite"
            >
              MonBeausite
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

// ============== HOME PAGE ==============

// Section pliable Voyance / Guidance (defaut: replie pour gagner de la place sur mobile)
function VoyanceGuidanceSection() {
  const [open, setOpen] = useState(false);
  return (
    <section className="py-12 md:py-16 relative overflow-hidden">
      <SectionAtmosphere variant="portal" stars={true} />
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <div className="relative rounded-3xl px-6 py-10 md:px-12 md:py-12 backdrop-blur-xl bg-[#261B3B]/40 border border-[#9D4CDD]/25 shadow-[0_0_50px_rgba(157,76,221,0.10)] overflow-hidden text-center">
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] to-transparent pointer-events-none" />
          <div className="absolute top-5 left-5 text-2xl opacity-15 select-none">☾</div>
          <div className="absolute bottom-5 right-5 text-2xl opacity-15 select-none">✦</div>
          <span className="text-[#D926A9] text-xs uppercase tracking-[0.3em] mb-3 block relative">Comprendre la différence</span>
          <h2 className="font-serif text-3xl md:text-4xl mb-2 relative">Voyance ou Guidance ?</h2>
          <p className="font-mystic text-xl md:text-2xl text-[#00E5FF] glow-cyan mb-4 relative">l'une révèle, l'autre éclaire</p>
          <p className="text-[#B59ED1] text-sm max-w-lg mx-auto mb-5 relative">
            Tu hésites entre les deux ? Découvre en quelques mots ce qui les différencie.
          </p>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            aria-controls="voyance-guidance-content"
            className="relative inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-[#D926A9]/40 text-[#F7F3FC] bg-[#1B132B]/60 hover:border-[#D926A9] hover:bg-[#D926A9]/10 transition-all text-sm"
            data-testid="toggle-voyance-guidance"
          >
            <Eye className="w-4 h-4 text-[#D926A9]" />
            <span className="font-medium">{open ? 'Masquer la différence' : 'Connaître la différence'}</span>
            <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
          </button>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              id="voyance-guidance-content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-6">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-[#261B3B] rounded-2xl p-5 md:p-6 border border-white/10 hover:border-[#D926A9]/30 transition-all"
                  data-testid="voyance-card"
                >
                  <div className="w-10 h-10 rounded-full bg-[#D926A9]/15 flex items-center justify-center mb-3">
                    <Eye className="w-5 h-5 text-[#D926A9]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#F7F3FC] mb-2">La Voyance</h3>
                  <p className="text-[#E2D5F8] leading-relaxed text-sm">
                    La Voyance permet de voyager dans le temps. C'est elle qui permet de remonter dans le passé et également de recevoir des prédictions. Lorsque tu demandes : « Comment évoluera ma vie professionnelle ? », la lecture s'imprègnera de tes énergies actuelles pour te donner des prédictions suivant le moment présent. La voyance reçue est toujours imprégnée de tes énergies au moment de la séance. Tu disposes toujours de ton libre-arbitre afin d'agir sur ton futur.
                  </p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 }}
                  className="bg-[#261B3B] rounded-2xl p-5 md:p-6 border border-white/10 hover:border-[#00E5FF]/30 transition-all"
                  data-testid="guidance-card"
                >
                  <div className="w-10 h-10 rounded-full bg-[#00E5FF]/15 flex items-center justify-center mb-3">
                    <Sparkles className="w-5 h-5 text-[#00E5FF]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#F7F3FC] mb-2">La Guidance</h3>
                  <p className="text-[#E2D5F8] leading-relaxed text-sm">
                    Lors d'une Guidance, c'est une aide sous forme de conseils ou de messages clés pour mieux appréhender ton futur qui te sont délivrés. Ainsi tu seras guidé·e et auras les outils adéquats pour faire tes choix et te diriger vers le chemin qui te convient.
                  </p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function LatestBlogSection() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/blog/articles`, { params: { limit: 3 } })
      .then(res => { if (mounted) setArticles(res.data.articles || []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (!loading && articles.length === 0) return null;

  return (
    <section className="py-24 relative overflow-hidden" data-testid="home-blog-section">
      <SectionAtmosphere variant="aurora-violet" stars={true} />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <SectionDivider icon={Sparkles} label="Inspirations & savoir" color="#00E5FF" />
          <h2 className="font-serif text-4xl md:text-5xl mb-3">Le journal de Bilan2vie</h2>
          <p className="text-[#B59ED1] max-w-2xl mx-auto">
            Articles, guides pratiques et rituels pour t'accompagner sur ton chemin spirituel.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {loading
            ? [0,1,2].map(i => <div key={i} className="h-80 skeleton rounded-2xl" />)
            : articles.map(article => (
              <article
                key={article.article_id || article.slug}
                onClick={() => navigate(`/blog/${article.slug}`)}
                className="group bg-[#261B3B] rounded-2xl overflow-hidden border border-white/10 hover:border-[#D926A9]/50 transition-all duration-300 cursor-pointer flex flex-col"
                data-testid={`home-blog-card-${article.slug}`}
              >
                {article.image_url && (
                  <div className="aspect-video overflow-hidden bg-[#1B132B]">
                    <img
                      src={article.image_url.startsWith('/') ? `${BACKEND_URL}${article.image_url}` : article.image_url}
                      alt={article.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <Badge className="bg-[#D926A9]/20 text-[#D926A9] w-fit mb-3">{article.category}</Badge>
                  <h3 className="font-serif text-xl mb-2 text-[#F7F3FC] group-hover:text-[#00E5FF] transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-[#B59ED1] line-clamp-3 flex-1">{article.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-[#00E5FF] text-sm font-medium opacity-80 group-hover:opacity-100 transition-opacity">
                    Lire l'article <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </article>
            ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#00E5FF] hover:text-white transition-colors text-sm font-medium"
            data-testid="home-blog-see-all"
          >
            Tous les articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [serviceModal, setServiceModal] = useState(null);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const shopHours = useShopHours();

  const EXCLUDED_VARIATIONS = ['Question Flash', 'Voyance/Guidance 15 Minutes', 'Guidance 15 Minutes'];

  // ============== Carrousel d'avis ==============
  const reviews = [
    { name: "Johanna M.", text: "Moi qui étais sceptique sur ce type de prestations, j'ai pu, grâce au professionnalisme de Guillaume, découvrir une méthode relaxante, qui a de nombreux bienfaits. Durant la séance, j'ai pu me déconnecter absolument totalement. Un moment unique que je ne peux que vous inviter à essayer. Merci encore à toi Guillaume pour ta bienveillance !", service: "Pranic Healing", rating: 5 },
    { name: "Sophie L.", text: "Excellente qualité d'écoute et un réel don. Guillaume est un praticien exceptionnel, très professionnel. La séance de voyance a été révélatrice et m'a apporté beaucoup de clarté sur ma situation. Je recommande les yeux fermés.", service: "Voyance & Guidance", rating: 5 },
    { name: "Marc D.", text: "Professionnel très gentil et à l'écoute. Un tirage complet qui prend le temps qu'il faut, nous avons mis 2h. Très intéressant, ça peut énormément vous aider. Guillaume a su trouver les mots justes pour me guider.", service: "Bilan de Vie", rating: 5 },
    { name: "Émilie R.", text: "Agréablement surprise de cette première séance de Pranic Healing. J'ai ressenti un profond apaisement et une détente que je n'avais pas connu depuis longtemps. Guillaume est bienveillant et prend le temps d'expliquer chaque étape du soin.", service: "Pranic Healing", rating: 5 },
    { name: "Laurent B.", text: "Un vrai professionnel passionné par ce qu'il fait. La boutique est magnifique, pleine de belles énergies. Les pierres sont de qualité et les conseils de Guillaume sont toujours pertinents. J'y retourne régulièrement.", service: "Boutique", rating: 5 },
    { name: "Nathalie P.", text: "Guillaume a un vrai don de médiumnité. Sa consultation de voyance m'a permis d'y voir plus clair dans ma vie professionnelle. Je suis ressortie avec une énergie nouvelle et des pistes concrètes. Merci pour cette belle expérience.", service: "Voyance & Guidance", rating: 5 },
  ];
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewsPerView, setReviewsPerView] = useState(3);

  useEffect(() => {
    const updatePerView = () => {
      const w = window.innerWidth;
      if (w < 768) setReviewsPerView(1);
      else if (w < 1024) setReviewsPerView(2);
      else setReviewsPerView(3);
    };
    updatePerView();
    window.addEventListener('resize', updatePerView);
    return () => window.removeEventListener('resize', updatePerView);
  }, []);

  const reviewMaxIdx = Math.max(0, reviews.length - reviewsPerView);
  useEffect(() => {
    if (reviewIndex > reviewMaxIdx) setReviewIndex(reviewMaxIdx);
  }, [reviewMaxIdx, reviewIndex]);

  // Auto-rotate toutes les 7s
  useEffect(() => {
    const t = setInterval(() => {
      setReviewIndex(i => i >= reviewMaxIdx ? 0 : i + 1);
    }, 7000);
    return () => clearInterval(t);
  }, [reviewMaxIdx]);

  const reviewPrev = () => setReviewIndex(i => i <= 0 ? reviewMaxIdx : i - 1);
  const reviewNext = () => setReviewIndex(i => i >= reviewMaxIdx ? 0 : i + 1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, productsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/products`)
        ]);
        setServices(servicesRes.data);
        const allInStock = productsRes.data.filter(p => (p.stock || 0) > 0);
        // Les "coups de cœur" de l'accueil = EXACTEMENT ceux marqués coup de cœur en boutique
        // (flag is_featured en base, piloté depuis la fiche produit admin), dans le même ordre.
        const featured = pickFeaturedForHome(productsRes.data);
        // On affiche tous les coups de cœur (plafonné à 8 pour garder la section lisible).
        // Repli uniquement si AUCUN coup de cœur n'est défini : quelques produits en stock.
        const display = featured.length > 0 ? featured.slice(0, 8) : allInStock.slice(0, 4);
        setProducts(display);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const serviceIcons = {
    "Voyance": <Eye className="w-6 h-6" />,
    "Soin Énergétique": <Heart className="w-6 h-6" />,
    "Soins Énergétiques": <Heart className="w-6 h-6" />,
    "Numérologie": <Star className="w-6 h-6" />,
    "Ateliers": <Sparkles className="w-6 h-6" />,
    voyance: <Eye className="w-6 h-6" />,
    soins: <Heart className="w-6 h-6" />,
    purification: <Shield className="w-6 h-6" />,
    numerologie: <Star className="w-6 h-6" />
  };

  return (
    <main>
      <Seo
        path="/"
        title="Voyance, Pranic Healing & Boutique Ésotérique à Montélimar (Drôme)"
        description="Consultations de voyance, soins Pranic Healing, numérologie et bilan de vie à Montélimar — sur place ou à distance partout en France. Boutique ésotérique : cristaux, encens, tarots, bougies."
        keywords="voyance Montélimar, voyance à distance, Pranic Healing, numérologie, bilan de vie, boutique ésotérique Drôme, cristaux, magnétiseur Montélimar"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Bilan 2 Vie",
          "url": SITE_URL,
          "inLanguage": "fr-FR",
          "publisher": { "@type": "Organization", "name": "Bilan 2 Vie" },
          "potentialAction": {
            "@type": "SearchAction",
            "target": SITE_URL + "/boutique?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      {/* Hero Section */}
      <section
        className="hero-bg min-h-screen flex items-center justify-center relative pt-36 sm:pt-32 md:pt-24"
        style={{ paddingTop: 'max(9rem, calc(env(safe-area-inset-top, 0px) + 6rem))' }}
        data-testid="hero-section"
      >
        {/* Floating Orbs */}
        <div className="orb orb-magenta w-96 h-96 -top-20 -left-20 animate-pulse-glow"></div>
        <div className="orb orb-cyan w-72 h-72 top-1/3 right-10 animate-pulse-glow" style={{ animationDelay: '1s' }}></div>
        <div className="orb orb-purple w-80 h-80 bottom-20 left-1/4 animate-pulse-glow" style={{ animationDelay: '2s' }}></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#00E5FF] text-sm uppercase tracking-[0.2em] mb-6 block">Boutique Spirituelle à Montélimar</span>
            <a
              href="https://share.google/gsMAN7kdqzvUWaIbJ"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 mb-8 rounded-full bg-white/5 backdrop-blur-sm border border-[#FFD700]/20 hover:border-[#FFD700]/40 hover:bg-white/10 transition-all group"
              data-testid="hero-trust-badge"
              aria-label="Voir nos avis Google"
            >
              <Star className="w-3.5 h-3.5 fill-[#FFD700] text-[#FFD700]" />
              <span className="text-xs text-[#E2D5F8]/90 tracking-wide">
                <strong className="text-white font-semibold">4,9/5</strong>
                <span className="mx-1.5 text-[#B59ED1]/60">·</span>
                139 avis Google
              </span>
            </a>
            <h1 className="font-serif text-4xl md:text-6xl leading-tight mb-8">
              Ta boutique spirituelle<br />
              <span className="bg-gradient-to-r from-[#D926A9] to-[#00E5FF] bg-clip-text text-transparent">& ton espace de guidance</span>
            </h1>
            <p className="text-[#E2D5F8] text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed">
              Pierres, encens, bougies, oracles et bijoux spirituels, ainsi que mes consultations de voyance et soins énergétiques — en boutique à Montélimar ou à distance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/booking')} 
                className="gradient-primary text-lg px-8 py-6 rounded-full hover:shadow-[0_0_30px_rgba(217,38,169,0.5)] transition-all"
                data-testid="cta-booking"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Réserver une consultation
              </Button>
              <Button 
                onClick={() => navigate('/boutique')} 
                variant="outline"
                className="border-[#00E5FF]/40 text-[#00E5FF] text-lg px-8 py-6 rounded-full hover:bg-[#00E5FF]/10"
                data-testid="cta-boutique"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Explorer la boutique
              </Button>
            </div>

            {/* Lien discret carte cadeau Square */}
            <a
              href="https://app.squareup.com/gift/MLXZPE5BYDD43/order"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 mt-8 text-sm text-[#B59ED1] hover:text-[#D926A9] transition-colors"
              data-testid="home-giftcard-link"
            >
              <Gift className="w-4 h-4" />
              <span className="border-b border-[#B59ED1]/30 group-hover:border-[#D926A9] transition-colors">
                Offrir une carte cadeau
              </span>
            </a>
          </motion.div>
        </div>

        {/* Scroll indicator - hidden on mobile */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronRight className="w-8 h-8 text-[#B59ED1] rotate-90" />
        </motion.div>
      </section>

      {/* Infos pratiques — bande fine sous le Hero */}
      <section
        className="relative border-y border-white/10 bg-[#1B132B]/70 backdrop-blur-sm"
        data-testid="practical-info-bar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-center md:gap-8 gap-2.5 text-xs md:text-sm text-[#B59ED1]">
            <div className="flex items-center gap-2 justify-center md:justify-start">
              <Info className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />
              <span className="uppercase tracking-[0.18em] text-[#E2D5F8] font-light text-[10px] md:text-[11px]">
                Infos avant ta visite
              </span>
            </div>
            <div className="hidden md:block w-px h-4 bg-white/10" />
            <div className="flex items-start md:items-center gap-2 justify-center md:justify-start">
              <Car className="w-3.5 h-3.5 text-[#D926A9] flex-shrink-0 mt-0.5 md:mt-0" />
              <span>
                <strong className="text-[#E2D5F8]">Parking gratuit du Roubion</strong> à 120 m.
              </span>
            </div>
            <div className="hidden md:block w-px h-4 bg-white/10" />
            <div className="flex items-start md:items-center gap-2 justify-center md:justify-start">
              <Clock className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0 mt-0.5 md:mt-0" />
              <span>
                {shopHours && Object.keys(shopHours).length > 0 ? (
                  <>Boutique ouverte <strong className="text-[#E2D5F8]">{shopDaysSummary(shopHours)}</strong>. Les <strong className="text-[#E2D5F8]">après-midis du mercredi et du samedi</strong> sont réservés à la vente en boutique (sans rendez-vous) — aucun risque de trouver porte close.</>
                ) : (
                  <>Boutique à Montélimar. Les <strong className="text-[#E2D5F8]">après-midis du mercredi et du samedi</strong> sont réservés à la vente en boutique (sans rendez-vous) — aucun risque de trouver porte close.</>
                )}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="pt-20 pb-24 relative overflow-hidden" data-testid="services-section">
        <SectionAtmosphere variant="aurora-cyan" stars={true} />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <SectionDivider icon={Sparkles} label="Mes Prestations" color="#D926A9" />
            <h2 className="font-serif text-4xl md:text-5xl">Soin Énergétique & Voyance</h2>
          </div>

          {/* Services principaux : Pranic Healing, Voyance/Guidance, Bilan de Vie */}
          {(() => {
            const HOMEPAGE_SERVICES = ['Séance Pranic Healing', 'Voyance/Guidance', 'Bilan de Vie'];
            const mainServices = services.filter(s => HOMEPAGE_SERVICES.includes(s.name));
            return (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {mainServices.map((service, index) => (
                    <motion.div
                      key={service.service_id}
                      className="group relative rounded-2xl overflow-hidden border border-[#D926A9]/30 cursor-pointer flex flex-col h-full bg-[#261B3B] hover:border-[#D926A9]/60 transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                      onClick={() => setServiceModal(service)}
                      data-testid={`service-card-${service.service_id}`}
                    >
                      <div className="relative h-64 overflow-hidden flex-shrink-0">
                        {service.image_url ? (
                          <img 
                            src={service.image_url} 
                            alt={service.name}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-br from-[#3A115E] to-[#261B3B]" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#261B3B] via-transparent to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <Badge className="bg-[#D926A9]/80 text-white text-[10px] mb-2 backdrop-blur-sm">{service.category}</Badge>
                          <h3 className="font-serif text-xl lg:text-2xl text-white drop-shadow-lg leading-tight">{service.name}</h3>
                        </div>
                      </div>
                      <div className="p-4 lg:p-5 flex-1 flex flex-col gap-3 bg-[#261B3B]">
                        {/* Mode + duree */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {service.is_async ? (
                            <span className="px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] text-[10px] font-medium border border-[#00E5FF]/20 inline-flex items-center"><Mail className="w-3 h-3 mr-1" />Par email sous {service.async_delivery_days || 3}j</span>
                          ) : (
                            <>
                              {service.location_modes?.length === 2 ? (
                                <span className="px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] text-[10px] font-medium border border-[#00E5FF]/20 inline-flex items-center"><MapPin className="w-3 h-3 mr-1" />En boutique ou à distance</span>
                              ) : service.location_modes?.includes('distance') ? (
                                <span className="px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] text-[10px] font-medium border border-[#00E5FF]/20 inline-flex items-center">À distance</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full bg-[#D926A9]/15 text-[#D926A9] text-[10px] font-medium border border-[#D926A9]/20 inline-flex items-center"><MapPin className="w-3 h-3 mr-1" />En boutique</span>
                              )}
                              <Badge className="bg-[#3A115E] text-[#E2D5F8] text-[10px] font-normal"><Clock className="w-3 h-3 mr-1" />{service.duration_minutes} min</Badge>
                            </>
                          )}
                        </div>
                        {/* Prix + CTA, ne wrap jamais */}
                        <div className="flex items-end justify-between gap-3 mt-auto">
                          <span className="text-[#00E5FF] font-semibold text-base lg:text-lg whitespace-nowrap">
                            {service.has_variations && service.variations?.length > 1
                              ? `Dès ${Math.min(...service.variations.filter(v => !EXCLUDED_VARIATIONS.includes(v.name)).map(v => v.price))} €`
                              : `${service.price} €`
                            }
                          </span>
                          <span className="text-[#D926A9] text-xs lg:text-sm font-medium group-hover:translate-x-1 transition-transform whitespace-nowrap">
                            Découvrir →
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Lien vers toutes les prestations */}
                <motion.div
                  className="text-center mt-12"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to="/services"
                    className="group inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/15 hover:border-[#D926A9]/40 bg-[#261B3B]/50 hover:bg-[#261B3B] backdrop-blur-sm transition-all duration-300"
                    data-testid="see-all-services-link"
                  >
                    <span className="text-[#E2D5F8] group-hover:text-white transition-colors">
                      Consultation par mail, Purification, Numérologie...
                    </span>
                    <ArrowRight className="w-4 h-4 text-[#D926A9] group-hover:translate-x-1 transition-transform" />
                    <span className="text-[#D926A9] font-medium">Voir toutes les prestations</span>
                  </Link>
                </motion.div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Service Detail Modal */}
      <AnimatePresence>
        {serviceModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setServiceModal(null)} />
            <motion.div
              className="relative bg-[#261B3B] rounded-2xl border border-white/15 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              data-testid="service-modal"
            >
              {/* Modal Image */}
              {serviceModal.image_url && (
                <div className="relative h-56 overflow-hidden rounded-t-2xl">
                  <img src={serviceModal.image_url} alt={serviceModal.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#261B3B] via-transparent to-transparent" />
                </div>
              )}
              
              {/* Close button */}
              <button
                onClick={() => setServiceModal(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors z-10"
                data-testid="service-modal-close"
              >
                ×
              </button>

              <div className="p-8">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs">{serviceModal.category}</Badge>
                  {serviceModal.location_modes?.length === 2 ? (
                    <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] text-xs border border-[#00E5FF]/20">
                      <MapPin className="w-3 h-3 mr-1" />En boutique ou à distance
                    </Badge>
                  ) : serviceModal.location_modes?.includes('distance') ? (
                    <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] text-xs border border-[#00E5FF]/20">
                      À distance uniquement
                    </Badge>
                  ) : (
                    <Badge className="bg-[#D926A9]/10 text-[#D926A9] text-xs border border-[#D926A9]/20">
                      <MapPin className="w-3 h-3 mr-1" />En boutique uniquement
                    </Badge>
                  )}
                </div>
                <h2 className="font-serif text-3xl text-[#F7F3FC] mb-4">{serviceModal.name}</h2>
                
                {/* Full Description (enrichie pour les services prioritaires) */}
                {(() => {
                  const enriched = enrichService(serviceModal);
                  return (
                    <>
                      <p className="text-[#E2D5F8] leading-relaxed mb-3 whitespace-pre-line">{enriched.description}</p>
                      {enriched.learnMore && (
                        <a
                          href={enriched.learnMore.url}
                          onClick={(e) => { e.preventDefault(); setServiceModal(null); navigate(enriched.learnMore.url); }}
                          className="inline-flex items-center gap-1 text-[#00E5FF] hover:text-[#D926A9] transition-colors text-sm mb-6 underline-offset-4 hover:underline"
                          data-testid="service-modal-learn-more"
                        >
                          {enriched.learnMore.label}
                        </a>
                      )}
                      {!enriched.learnMore && <div className="mb-6" />}
                    </>
                  );
                })()}

                {/* Variations / Formules */}
                {serviceModal.has_variations && serviceModal.variations?.length > 1 && (
                  <div className="mb-6 space-y-2">
                    <h3 className="text-sm uppercase tracking-wider text-[#B59ED1] mb-3">Formules disponibles</h3>
                    {serviceModal.variations
                      .filter(v => !['Question Flash', 'Voyance/Guidance 15 Minutes', 'Guidance 15 Minutes'].includes(v.name))
                      .map((v) => (
                      <div key={v.variation_id} className="flex items-center justify-between py-3 px-4 rounded-lg bg-[#1B132B] border border-white/5">
                        <span className="text-[#E2D5F8]">{v.name}</span>
                        <span className="text-[#D926A9] font-semibold">{v.price} €</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Price + CTA */}
                <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-serif text-[#00E5FF]">
                        {serviceModal.has_variations && serviceModal.variations?.length > 1
                          ? `À partir de ${Math.min(...serviceModal.variations.filter(v => !['Question Flash', 'Voyance/Guidance 15 Minutes', 'Guidance 15 Minutes'].includes(v.name)).map(v => v.price))} €`
                          : `${serviceModal.price} €`
                        }
                      </span>
                      <Badge className="bg-[#3A115E] text-[#E2D5F8]">
                        <Clock className="w-3 h-3 mr-1" />
                        {serviceModal.duration_minutes} min
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    onClick={() => {
                      setServiceModal(null);
                      if (serviceModal.is_quote) {
                        navigate('/devis', { state: { service: serviceModal } });
                      } else {
                        navigate('/booking', { state: { service: serviceModal } });
                      }
                    }}
                    className="ml-auto gradient-primary h-12 px-8"
                    data-testid="service-modal-book"
                  >
                    {serviceModal.is_quote ? 'Demander un devis' : 'Réserver'} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Voyance ou Guidance ? — collapsible */}
      <VoyanceGuidanceSection />

      {/* À propos — mini-bloc avec lien vers /qui-suis-je */}
      <section className="relative py-16 md:py-20 overflow-hidden" data-testid="about-mini-section">
        <div className="absolute -top-[10%] left-0 w-[400px] h-[400px] halo-corner-bl blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-noise pointer-events-none" />
        <span className="deco-star magenta" style={{ top: '20%', right: '15%' }} />
        <span className="deco-star cyan" style={{ bottom: '25%', right: '8%' }} />

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center"
          >
            {/* Photo neon ronde */}
            <div className="relative w-[180px] h-[180px] md:w-[220px] md:h-[220px] mx-auto md:mx-0 flex-shrink-0">
              <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-[#D926A9]/40 via-[#9D4CDD]/30 to-[#00E5FF]/30 blur-2xl animate-pulse-glow" />
              <img
                src="https://customer-assets.emergentagent.com/job_spirituel-wellness/artifacts/wikfpu6u_Photo%20de%20profil%20YouTube%202.PNG"
                alt="Guillaume — Médium à Montélimar"
                className="relative rounded-full w-full h-full object-cover border-2 border-white/15 shadow-[0_0_30px_rgba(217,38,169,0.3)]"
                data-testid="home-about-photo"
              />
            </div>

            {/* Texte */}
            <div className="text-center md:text-left">
              <Badge className="bg-[#D926A9]/15 text-[#D926A9] border border-[#D926A9]/30 mb-3 text-[10px] uppercase tracking-[0.25em]">
                Qui suis-je ?
              </Badge>
              <h2 className="font-serif text-3xl md:text-4xl mb-3">Guillaume, ton médium</h2>
              <p className="font-mystic text-lg md:text-xl text-[#00E5FF] glow-cyan mb-4">
                des racines mauriciennes, une mission claire
              </p>
              <p className="text-[#E2D5F8] leading-relaxed mb-6 max-w-2xl">
                Issu d'une famille spirituelle depuis plusieurs générations, je me suis tourné vers la spiritualité <strong className="text-[#F7F3FC]">depuis 2015</strong> et j'en ai fait mon <strong className="text-[#F7F3FC]">métier depuis 2021</strong> — voyance, Pranic Healing et guidance. Mon approche unique mêle <strong className="text-[#F7F3FC]">spiritualité, médiumnité et écoute bienveillante</strong>.
              </p>
              <Link to="/qui-suis-je">
                <Button
                  variant="outline"
                  className="border-[#D926A9]/50 text-[#D926A9] hover:bg-[#D926A9]/10 hover:border-[#D926A9] transition-all"
                  data-testid="home-about-cta"
                >
                  Mieux me connaître
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="pt-24 pb-32 relative overflow-hidden" data-testid="products-section">
        <SectionAtmosphere variant="surface" stars={true} />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <SectionDivider icon={Sparkles} label="La Boutique" color="#D926A9" />
            <h2 className="font-serif text-4xl md:text-5xl mb-3">Articles Spirituels</h2>
            <p className="text-[#B59ED1] max-w-2xl mx-auto mb-4 text-sm md:text-base">
              Une sélection coup de cœur, soigneusement choisie — expédiée depuis ma boutique de Montélimar.
            </p>
            <Button 
              onClick={() => navigate('/boutique')}
              variant="ghost" 
              className="text-[#00E5FF] hover:text-[#00E5FF]/80"
              data-testid="view-all-products"
            >
              Voir tout <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Visuel boutique — photo réelle de la boutique de Montélimar, en arrière-plan immersif
              entre le titre et la grille produits. Effet "fenêtre cintrée" avec gradient progressif
              pour fondre l'image dans le fond mauve foncé du site. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative mb-12 mx-auto max-w-5xl rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(217,38,169,0.18)]"
            data-testid="boutique-hero-photo"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_spirituel-wellness/artifacts/64vi7hf9_Photo%20boutique%20arrondi.png"
              alt="Intérieur de la boutique Bilan 2 Vie à Montélimar — cristaux, bougies, bracelets et ambiance lumière bleutée"
              className="w-full h-auto object-cover max-h-[420px] md:max-h-[500px]"
              loading="lazy"
            />
            {/* Overlay gradient pour fondre dans le fond + lisibilité du caption */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1B132B] via-[#1B132B]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 px-6 py-5 text-center">
              <p className="font-serif text-base md:text-lg text-white drop-shadow-lg">
                <span className="text-[#00E5FF]">✨</span> Notre boutique à Montélimar <span className="text-[#00E5FF]">✨</span>
              </p>
              <p className="text-[#E2D5F8]/90 text-xs md:text-sm mt-1 drop-shadow">
                20 quai du Roubion — cristaux, bougies, encens & rituels
              </p>
            </div>
          </motion.div>

          {/* Bandeau confiance */}
          <TrustStrip />

          {/* Mobile-only quick category chips */}
          <div className="sm:hidden -mx-6 px-6 mb-6">
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {[
                { key: "Pierres & Cristaux", label: "Pierres" },
                { key: "Bijoux Spirituels", label: "Bijoux" },
                { key: "Encens & Purification", label: "Encens" },
                { key: "Bougies & Rituels", label: "Bougies" },
                { key: "Oracles & Tarots", label: "Oracles" },
                { key: "Huiles & Essences", label: "Huiles" },
                { key: "Bols & Sons", label: "Bols" },
                { key: "Coffrets & Cadeaux", label: "Coffrets" },
              ].map(c => (
                <button
                  key={c.key}
                  onClick={() => navigate(`/boutique?category=${encodeURIComponent(c.key)}`)}
                  className="shrink-0 px-3 py-1.5 rounded-full border border-white/15 bg-[#261B3B] text-[#E2D5F8] text-xs hover:border-[#D926A9]/40 hover:text-white transition-all"
                  data-testid={`home-cat-chip-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="product-grid">
            {products.map((product, index) => {
              const out = (product.stock || 0) <= 0;
              const featured = isFeatured(product);
              return (
              <motion.div
                key={product.product_id}
                className="product-card cursor-pointer relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(`/boutique/${product.product_id}`)}
                data-testid={`home-product-${product.product_id}`}
              >
                <div className="aspect-square overflow-hidden relative">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  {featured && (
                    <div className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#D926A9] to-[#9D4CDD] text-white text-[10px] font-medium uppercase tracking-wider shadow-lg flex items-center gap-1" data-testid="featured-badge">
                      <Heart className="w-3 h-3 fill-white" />
                      Coup de cœur
                    </div>
                  )}
                  {out && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="text-white font-medium text-sm">Rupture de stock</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <Badge className="bg-[#3A115E] text-[#E2D5F8] mb-3">{product.category}</Badge>
                  <h3 className="font-serif text-xl mb-2">
                    <Link to={`/boutique/${product.product_id}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#D926A9] transition-colors">{product.name}</Link>
                  </h3>
                  <p className="text-[#B59ED1] text-sm mb-4 line-clamp-2">{stripMarkdown(product.short_description)}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-serif text-[#D926A9]">{product.price.toFixed(2)} €</span>
                    <Button 
                      onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                      size="sm" 
                      className="add-to-cart gradient-primary"
                      disabled={out}
                      data-testid={`add-cart-${product.product_id}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Avis & Témoignages */}
      <section className="py-24 relative overflow-hidden" data-testid="reviews-section">
        <SectionAtmosphere variant="aurora-gold" stars={true} />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <SectionDivider icon={Star} label="Témoignages" color="#FFD700" />
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Ce que disent mes clients</h2>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-[#E2D5F8] text-sm ml-2">4,9/5 sur 139 avis Google</span>
            </div>
          </div>

          {/* Carrousel d'avis */}
          <div className="relative" data-testid="reviews-carousel">
            {/* Fleche gauche */}
            <button
              onClick={reviewPrev}
              aria-label="Avis précédent"
              className="absolute -left-2 lg:-left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-[#261B3B] border border-white/10 hover:border-[#D926A9]/60 hover:bg-[#3A115E] text-white flex items-center justify-center transition-all shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="reviews-prev"
              disabled={reviews.length <= reviewsPerView}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Viewport */}
            <div className="overflow-hidden">
              <motion.div
                className="flex"
                animate={{ x: `-${reviewIndex * (100 / reviewsPerView)}%` }}
                transition={{ type: "spring", stiffness: 200, damping: 30 }}
              >
                {reviews.map((review, index) => (
                  <div
                    key={`${review.name}-${review.service}`}
                    className="flex-shrink-0 px-3"
                    style={{ width: `${100 / reviewsPerView}%` }}
                  >
                    <div
                      className="bg-[#261B3B] rounded-2xl p-6 border border-white/10 hover:border-[#D926A9]/30 transition-all duration-300 flex flex-col h-full min-h-[280px]"
                      data-testid={`review-card-${index}`}
                    >
                      <div className="flex items-center gap-1 mb-4">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className={`w-4 h-4 ${i <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-white/20'}`} />
                        ))}
                      </div>
                      <blockquote className="text-[#E2D5F8] text-sm leading-relaxed mb-6 flex-1">
                        &laquo;&nbsp;{review.text}&nbsp;&raquo;
                      </blockquote>
                      <div className="flex items-center justify-between pt-4 border-t border-white/10">
                        <div>
                          <p className="font-medium text-[#F7F3FC]">{review.name}</p>
                          <p className="text-xs text-[#B59ED1]">{review.service}</p>
                        </div>
                        <div className="flex items-center gap-1.5 text-[#B59ED1]">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                          <span className="text-xs">Google</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Fleche droite */}
            <button
              onClick={reviewNext}
              aria-label="Avis suivant"
              className="absolute -right-2 lg:-right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-[#261B3B] border border-white/10 hover:border-[#D926A9]/60 hover:bg-[#3A115E] text-white flex items-center justify-center transition-all shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="reviews-next"
              disabled={reviews.length <= reviewsPerView}
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Pagination dots */}
            {reviewMaxIdx > 0 && (
              <div className="flex items-center justify-center gap-2 mt-8" data-testid="reviews-dots">
                {Array.from({ length: reviewMaxIdx + 1 }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setReviewIndex(i)}
                    aria-label={`Aller à la diapositive ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${reviewIndex === i ? 'w-8 bg-[#D926A9]' : 'w-2 bg-white/20 hover:bg-white/40'}`}
                    data-testid={`reviews-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Latest blog articles - internal SEO linking */}
      <LatestBlogSection />
    </main>
  );
}

// ============== SERVICES PAGE ==============

function PrivateEventCard({ onClick, testId, badge, title, subtitle, image, startingPrice, ctaLabel }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group cursor-pointer rounded-2xl overflow-hidden border border-[#D926A9]/25 hover:border-[#D926A9]/60 transition-all duration-300 flex flex-col h-full bg-[#261B3B]"
      onClick={onClick}
      data-testid={testId}
    >
      <div className="relative h-56 overflow-hidden flex-shrink-0">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#261B3B] via-[#261B3B]/30 to-transparent" />
        <Badge className="absolute top-3 left-3 bg-[#D926A9] text-white text-[10px] backdrop-blur-sm">{badge}</Badge>
        <div className="absolute bottom-4 left-4 right-4">
          <h2 className="font-serif text-xl lg:text-2xl text-white drop-shadow-lg leading-tight">{title}</h2>
        </div>
      </div>
      <div className="p-4 lg:p-5 flex-1 flex flex-col gap-3 bg-[#261B3B]">
        <p className="text-[#B59ED1] text-sm leading-relaxed">{subtitle}</p>
        <div className="flex items-end justify-between gap-3 mt-auto">
          <span className="text-[#00E5FF] font-semibold text-base lg:text-lg whitespace-nowrap">
            Dès {startingPrice} € / h
          </span>
          <span className="text-xs lg:text-sm font-medium whitespace-nowrap text-[#D926A9] group-hover:translate-x-1 transition-transform">
            {ctaLabel}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceModal, setServiceModal] = useState(null);
  const navigate = useNavigate();

  const EXCLUDED_VARIATIONS = ['Question Flash', 'Voyance/Guidance 15 Minutes', 'Guidance 15 Minutes'];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des services");
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <main className="pt-24 min-h-screen">
      <Seo
        path="/services"
        title="Voyance, Pranic Healing & Numérologie — Sur place à Montélimar ou à distance"
        description="Découvrez les consultations de Bilan 2 Vie : voyance, Pranic Healing, numérologie, bilan de vie. En présentiel à Montélimar (Drôme) ou à distance partout en France."
        keywords="consultation voyance, Pranic Healing Drôme, numérologue Montélimar, bilan de vie, guidance spirituelle en ligne, voyance téléphone, soin énergétique distance"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": services.map((s) => ({
            "@type": "Service",
            "name": s.name,
            "description": s.description || s.name,
            "provider": { "@type": "LocalBusiness", "name": "Bilan 2 Vie", "address": { "@type": "PostalAddress", "addressLocality": "Montélimar", "postalCode": "26200", "addressCountry": "FR" } },
            "areaServed": (s.location_modes || []).includes("distance")
              ? [{ "@type": "Country", "name": "France" }, { "@type": "City", "name": "Montélimar" }]
              : { "@type": "City", "name": "Montélimar" },
            "serviceType": s.category || "Service spirituel",
            "offers": { "@type": "Offer", "price": s.price, "priceCurrency": "EUR", "availability": "https://schema.org/InStock" }
          }))
        }}
      />
      <section className="py-16 bg-[#1B132B]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <SectionDivider icon={Sparkles} label="Découvrez" color="#D926A9" />
            <h1 className="font-serif text-5xl md:text-6xl mb-6">Mes Prestations</h1>
            <p className="text-[#B59ED1] max-w-2xl mx-auto">
              Chaque consultation est un voyage personnalisé vers ton bien-être intérieur. Explore mes différentes prestations pour trouver celle qui résonne avec tes besoins.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-80 rounded-2xl skeleton"></div>
              ))}
            </div>
          ) : (() => {
            const FEATURED_NAMES = ['Séance Pranic Healing', 'Voyance/Guidance', 'Bilan de Vie'];
            const CATEGORY_ORDER = ['Voyance', 'Soin Énergétique', 'Soins Énergétiques', 'Purification', 'Numérologie'];
            const featured = services.filter(s => FEATURED_NAMES.includes(s.name))
              .sort((a, b) => (CATEGORY_ORDER.indexOf(a.category) === -1 ? 99 : CATEGORY_ORDER.indexOf(a.category)) - (CATEGORY_ORDER.indexOf(b.category) === -1 ? 99 : CATEGORY_ORDER.indexOf(b.category)));
            const purifications = services.filter(s => !FEATURED_NAMES.includes(s.name) && s.category === 'Purification');
            const autres = services.filter(s => !FEATURED_NAMES.includes(s.name) && s.category !== 'Purification');

            const renderCard = (service, index, isFeatured) => (
              <motion.div
                key={service.service_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`group cursor-pointer rounded-2xl overflow-hidden border ${isFeatured ? 'border-[#D926A9]/30 hover:border-[#D926A9]/60' : 'border-white/10 hover:border-[#D926A9]/30'} transition-all duration-300 flex flex-col h-full bg-[#261B3B]`}
                onClick={() => setServiceModal(service)}
                data-testid={`service-card-${service.service_id}`}
              >
                <div className={`relative ${isFeatured ? 'h-64' : 'h-56'} overflow-hidden flex-shrink-0`}>
                  {service.image_url ? (
                    <img src={service.image_url} alt={service.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#3A115E] to-[#261B3B] flex items-center justify-center"><Sparkles className="w-16 h-16 text-[#D926A9]" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#261B3B] via-transparent to-transparent" />
                  {/* Un seul badge top-left pour eviter les chevauchements en tablette */}
                  {isFeatured ? (
                    <Badge className="absolute top-3 left-3 bg-[#D926A9] text-white text-[10px] backdrop-blur-sm">Service phare</Badge>
                  ) : (
                    <Badge className="absolute top-3 left-3 bg-[#3A115E]/80 text-[#E2D5F8] backdrop-blur-sm text-[10px]">{service.category}</Badge>
                  )}
                  <div className="absolute bottom-4 left-4 right-4">
                    {isFeatured && <Badge className="bg-[#D926A9]/80 text-white text-[10px] mb-2 backdrop-blur-sm">{service.category}</Badge>}
                    <h2 className="font-serif text-xl lg:text-2xl text-white drop-shadow-lg leading-tight">{service.name}</h2>
                  </div>
                </div>
                <div className="p-4 lg:p-5 flex-1 flex flex-col gap-3 bg-[#261B3B]">
                  {/* Mode (en boutique / a distance) integre dans le footer */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {service.is_async ? (
                      <span className="px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] text-[10px] font-medium border border-[#00E5FF]/20 inline-flex items-center"><Mail className="w-3 h-3 mr-1" />Réponse par email sous {service.async_delivery_days || 3}j</span>
                    ) : service.is_quote ? (
                      <span className="px-2 py-0.5 rounded-full bg-[#D926A9]/15 text-[#D926A9] text-[10px] font-medium border border-[#D926A9]/20 inline-flex items-center"><MapPin className="w-3 h-3 mr-1" />À domicile · Sur devis</span>
                    ) : (
                      <>
                        {service.location_modes?.length === 2 ? (
                          <span className="px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] text-[10px] font-medium border border-[#00E5FF]/20 inline-flex items-center"><MapPin className="w-3 h-3 mr-1" />En boutique ou à distance</span>
                        ) : service.location_modes?.includes('distance') ? (
                          <span className="px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] text-[10px] font-medium border border-[#00E5FF]/20 inline-flex items-center">À distance</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-[#D926A9]/15 text-[#D926A9] text-[10px] font-medium border border-[#D926A9]/20 inline-flex items-center"><MapPin className="w-3 h-3 mr-1" />En boutique</span>
                        )}
                        <Badge className="bg-[#3A115E] text-[#E2D5F8] text-[10px] font-normal"><Clock className="w-3 h-3 mr-1" />{service.duration_minutes} min</Badge>
                      </>
                    )}
                  </div>
                  {/* Prix + CTA sur une ligne dediee qui ne wrap jamais */}
                  <div className="flex items-end justify-between gap-3 mt-auto">
                    <span className="text-[#00E5FF] font-semibold text-base lg:text-lg whitespace-nowrap">
                      {service.is_quote
                        ? `Dès ${service.quote_starting_price || service.price} €`
                        : service.has_variations && service.variations?.length > 1
                          ? `Dès ${Math.min(...service.variations.filter(v => !EXCLUDED_VARIATIONS.includes(v.name)).map(v => v.price))} €`
                          : `${service.price} €`
                      }
                    </span>
                    <span className={`text-xs lg:text-sm font-medium whitespace-nowrap ${isFeatured ? 'text-[#D926A9] group-hover:translate-x-1 transition-transform' : 'text-[#B59ED1] group-hover:text-[#D926A9] transition-colors'}`}>
                      {service.is_quote ? 'Demander un devis →' : (isFeatured ? 'Découvrir →' : 'En savoir plus →')}
                    </span>
                  </div>
                </div>
              </motion.div>
            );

            return (
              <>
                {/* Services Phares */}
                {featured.length > 0 && (
                  <div className="mb-12">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-px flex-1 bg-gradient-to-r from-[#D926A9]/50 to-transparent"></div>
                      <span className="text-[#D926A9] text-sm uppercase tracking-[0.15em] font-medium">Services phares</span>
                      <div className="h-px flex-1 bg-gradient-to-l from-[#D926A9]/50 to-transparent"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {featured.map((s, i) => renderCard(s, i, true))}
                    </div>
                  </div>
                )}

                {/* Purification */}
                {purifications.length > 0 && (
                  <div className="mb-12">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-px flex-1 bg-gradient-to-r from-[#00E5FF]/30 to-transparent"></div>
                      <Shield className="w-4 h-4 text-[#00E5FF]" />
                      <span className="text-[#00E5FF] text-sm uppercase tracking-[0.15em] font-medium">Purification</span>
                      <div className="h-px flex-1 bg-gradient-to-l from-[#00E5FF]/30 to-transparent"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {purifications.map((s, i) => renderCard(s, i, false))}
                    </div>
                  </div>
                )}

                {/* Autres */}
                {(autres.length > 0 || true) && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent"></div>
                      <span className="text-[#B59ED1] text-sm uppercase tracking-[0.15em]">Autres prestations</span>
                      <div className="h-px flex-1 bg-gradient-to-l from-white/15 to-transparent"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Cartes statiques événementiels (pages SEO dédiées) */}
                      <PrivateEventCard
                        onClick={() => navigate("/services/evjf")}
                        testId="service-card-evjf"
                        badge="Événementiel"
                        title="EVJF / EVJG Voyance & Tarot"
                        subtitle="Animation originale pour enterrement de vie de jeune fille"
                        image="https://customer-assets.emergentagent.com/job_spirituel-wellness/artifacts/fq3nq1jr_EVJF%20-%20Grande.jpeg"
                        startingPrice={70}
                        ctaLabel="Découvrir →"
                      />
                      <PrivateEventCard
                        onClick={() => navigate("/services/soiree-privee")}
                        testId="service-card-soiree-privee"
                        badge="À domicile"
                        title="Soirée voyance privée à domicile"
                        subtitle="Anniversaire, Pleine Lune, mariage, événement entre amies"
                        image="https://customer-assets.emergentagent.com/job_spirituel-wellness/artifacts/nk6rmmbw_soire%CC%81es%20prive%CC%81es%20-%20Grande.jpeg"
                        startingPrice={70}
                        ctaLabel="Découvrir →"
                      />
                      {autres.map((s, i) => renderCard(s, i, false))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* Service Detail Modal */}
      <AnimatePresence>
        {serviceModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setServiceModal(null)} />
            <motion.div
              className="relative bg-[#261B3B] rounded-2xl border border-white/15 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              data-testid="service-modal"
            >
              {serviceModal.image_url && (
                <div className="relative h-56 overflow-hidden rounded-t-2xl">
                  <img src={serviceModal.image_url} alt={serviceModal.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#261B3B] via-transparent to-transparent" />
                </div>
              )}
              <button
                onClick={() => setServiceModal(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors z-10"
                data-testid="service-modal-close"
              >
                ×
              </button>
              <div className="p-8">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs">{serviceModal.category}</Badge>
                  {serviceModal.location_modes?.length === 2 ? (
                    <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] text-xs border border-[#00E5FF]/20">
                      <MapPin className="w-3 h-3 mr-1" />En boutique ou à distance
                    </Badge>
                  ) : serviceModal.location_modes?.includes('distance') ? (
                    <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] text-xs border border-[#00E5FF]/20">
                      À distance uniquement
                    </Badge>
                  ) : (
                    <Badge className="bg-[#D926A9]/10 text-[#D926A9] text-xs border border-[#D926A9]/20">
                      <MapPin className="w-3 h-3 mr-1" />En boutique uniquement
                    </Badge>
                  )}
                </div>
                <h2 className="font-serif text-3xl text-[#F7F3FC] mb-4">{serviceModal.name}</h2>
                {(() => {
                  const enriched = enrichService(serviceModal);
                  return (
                    <>
                      <p className="text-[#E2D5F8] leading-relaxed mb-3 whitespace-pre-line">{enriched.description}</p>
                      {enriched.learnMore && (
                        <a
                          href={enriched.learnMore.url}
                          onClick={(e) => { e.preventDefault(); setServiceModal(null); navigate(enriched.learnMore.url); }}
                          className="inline-flex items-center gap-1 text-[#00E5FF] hover:text-[#D926A9] transition-colors text-sm mb-6 underline-offset-4 hover:underline"
                          data-testid="services-modal-learn-more"
                        >
                          {enriched.learnMore.label}
                        </a>
                      )}
                      {!enriched.learnMore && <div className="mb-6" />}
                    </>
                  );
                })()}

                {serviceModal.has_variations && serviceModal.variations?.length > 1 && (
                  <div className="mb-6 space-y-2">
                    <h3 className="text-sm uppercase tracking-wider text-[#B59ED1] mb-3">Formules disponibles</h3>
                    {serviceModal.variations
                      .filter(v => !EXCLUDED_VARIATIONS.includes(v.name))
                      .map((v) => (
                      <div key={v.variation_id} className="flex items-center justify-between py-3 px-4 rounded-lg bg-[#1B132B] border border-white/5">
                        <span className="text-[#E2D5F8]">{v.name}</span>
                        <span className="text-[#D926A9] font-semibold">{v.price} €</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-serif text-[#00E5FF]">
                      {serviceModal.has_variations && serviceModal.variations?.length > 1
                        ? `À partir de ${Math.min(...serviceModal.variations.filter(v => !EXCLUDED_VARIATIONS.includes(v.name)).map(v => v.price))} €`
                        : `${serviceModal.price} €`
                      }
                    </span>
                    <Badge className="bg-[#3A115E] text-[#E2D5F8]">
                      <Clock className="w-3 h-3 mr-1" />
                      {serviceModal.duration_minutes} min
                    </Badge>
                  </div>
                  <Button 
                    onClick={() => {
                      setServiceModal(null);
                      if (serviceModal.is_quote) {
                        navigate('/devis', { state: { service: serviceModal } });
                      } else {
                        navigate('/booking', { state: { service: serviceModal } });
                      }
                    }}
                    className="ml-auto gradient-primary h-12 px-8"
                    data-testid="service-modal-book"
                  >
                    {serviceModal.is_quote ? 'Demander un devis' : 'Réserver'} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}



// ============== BLOG PAGE ==============

function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = { limit: 12, skip: page * 12 };
        if (selectedCategory) params.category = selectedCategory;
        const [articlesRes, catsRes] = await Promise.all([
          axios.get(`${API}/blog/articles`, { params }),
          axios.get(`${API}/blog/categories`)
        ]);
        setArticles(articlesRes.data.articles || []);
        setTotal(articlesRes.data.total || 0);
        setCategories(catsRes.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [selectedCategory, page]);

  return (
    <main className="pt-24 min-h-screen">
      <Seo
        path="/blog"
        title="Blog spirituel — Voyance, Pranic Healing & Énergies"
        description="Découvre des articles inspirants sur la voyance, les soins énergétiques, le Pranic Healing, la numérologie et le développement spirituel. Des conseils pour ton éveil intérieur."
        keywords="blog voyance, blog spiritualité, Pranic Healing, énergie, cristaux, développement personnel spirituel, numérologie"
      />
      <section className="py-16 bg-[#1B132B]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <SectionDivider icon={Sparkles} label="Inspirations & Savoir" color="#00E5FF" />
            <h1 className="font-serif text-4xl sm:text-5xl mb-4">Le Blog</h1>
            <p className="text-[#B59ED1] max-w-xl mx-auto text-sm">
              Explore des articles inspirants sur la voyance, la spiritualite, les soins energetiques et le developpement personnel.
            </p>
          </div>

          {/* Categories */}
          {categories.length > 0 && (
            <div className="flex gap-2 justify-center flex-wrap mb-10">
              <button onClick={() => { setSelectedCategory(''); setPage(0); }}
                className={`px-4 py-2 rounded-full text-sm transition-all ${!selectedCategory ? 'bg-[#D926A9] text-white' : 'bg-[#261B3B] text-[#B59ED1] hover:text-white border border-white/10'}`}
                data-testid="blog-cat-all">Tous</button>
              {categories.map(c => (
                <button key={c.category} onClick={() => { setSelectedCategory(c.category); setPage(0); }}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${selectedCategory === c.category ? 'bg-[#D926A9] text-white' : 'bg-[#261B3B] text-[#B59ED1] hover:text-white border border-white/10'}`}
                  data-testid={`blog-cat-${c.category}`}>{c.category} ({c.count})</button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => <div key={i} className="h-80 rounded-2xl skeleton" />)}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <Sparkles className="w-12 h-12 text-[#D926A9] mx-auto mb-4" />
              <p className="text-[#B59ED1] text-lg">Les articles arrivent bientot...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article, idx) => (
                  <motion.article
                    key={article.article_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    className="group cursor-pointer rounded-2xl overflow-hidden border border-white/10 hover:border-[#D926A9]/30 transition-all bg-[#261B3B]"
                    onClick={() => navigate(`/blog/${article.slug}`)}
                    data-testid={`blog-card-${article.article_id}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      {article.image_url ? (
                        <img src={article.image_url.startsWith('/') ? `${BACKEND_URL}${article.image_url}` : article.image_url} alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#3A115E] to-[#261B3B] flex items-center justify-center"><Sparkles className="w-12 h-12 text-[#D926A9]" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#261B3B] via-transparent to-transparent" />
                      <Badge className="absolute top-3 left-3 bg-[#D926A9]/80 text-white text-xs backdrop-blur-sm">{article.category}</Badge>
                      {article.is_featured && (
                        <Badge className="absolute top-3 right-3 bg-[#00E5FF]/90 text-[#1B132B] text-[10px] font-bold backdrop-blur-sm flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" /> À la une
                        </Badge>
                      )}
                    </div>
                    <div className="p-5">
                      <h2 className="font-serif text-lg mb-2 line-clamp-2 group-hover:text-[#D926A9] transition-colors">{article.title}</h2>
                      <p className="text-[#B59ED1] text-sm line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-[#B59ED1]">
                        <span>{article.published_at && new Date(article.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        <span className="text-[#D926A9]">Lire l'article →</span>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
              {total > 12 && (
                <div className="flex justify-center gap-2 mt-8">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="border-white/10 text-[#B59ED1]">Precedent</Button>
                  <span className="text-sm text-[#B59ED1] px-3 py-2">{page + 1} / {Math.ceil(total / 12)}</span>
                  <Button variant="outline" size="sm" disabled={(page + 1) * 12 >= total} onClick={() => setPage(p => p + 1)} className="border-white/10 text-[#B59ED1]">Suivant</Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function LegacyPostRedirect() {
  // SEO: keep old Wix /post/<slug> URLs alive by redirecting to /blog/<slug>.
  // <Navigate replace> rewrites the history entry so Google treats it like a 301.
  // A canonical tag on the target /blog/<slug> page consolidates link equity.
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug}`} replace />;
}

function LegacyServicePageRedirect() {
  // Wix /service-page/<slug> -> /services or /evenements (workshops) or /voyance-a-distance
  const { slug } = useParams();
  return <Navigate to={resolveServicePage(slug)} replace />;
}

function LegacyEventDetailsRedirect() {
  // Wix /event-details/<eventSlug> -> /evenements (all past events redirect to live list)
  return <Navigate to="/evenements" replace />;
}

function LegacyStaticRedirect({ to }) {
  // Generic 1:1 Wix → new path redirect (used for /book-online, Montélimar pages, etc.)
  return <Navigate to={to} replace />;
}

function BlogArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(res.data);
      } catch (e) { navigate('/blog'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [slug, navigate]);

  if (loading) return <main className="pt-24 min-h-screen bg-[#1B132B]"><div className="max-w-3xl mx-auto px-6 py-16"><div className="h-8 w-2/3 skeleton mb-4 rounded" /><div className="h-64 skeleton rounded-2xl mb-6" /><div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-4 skeleton rounded" />)}</div></div></main>;
  if (!article) return null;

  return (
    <main className="pt-24 min-h-screen bg-[#1B132B]">
      <Seo
        path={`/blog/${article.slug}`}
        title={article.meta_title || article.title}
        description={article.meta_description || article.excerpt || article.title}
        keywords={(article.keywords || []).join(', ')}
        image={article.image_url ? (article.image_url.startsWith('/') ? `${BACKEND_URL}${article.image_url}` : article.image_url) : undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt || article.meta_description,
          "image": article.image_url ? (article.image_url.startsWith('/') ? `${BACKEND_URL}${article.image_url}` : article.image_url) : undefined,
          "datePublished": article.published_at,
          "dateModified": article.updated_at || article.published_at,
          "author": { "@type": "Organization", "name": "Bilan 2 Vie" },
          "publisher": { "@type": "Organization", "name": "Bilan 2 Vie", "logo": { "@type": "ImageObject", "url": SITE_URL + "/logo.png" } },
          "mainEntityOfPage": { "@type": "WebPage", "@id": SITE_URL + "/blog/" + article.slug },
          "keywords": (article.keywords || []).join(', ')
        }}
      />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <button onClick={() => navigate('/blog')} className="flex items-center gap-2 text-[#B59ED1] hover:text-white mb-6 text-sm transition-colors" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> Retour au blog
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-[#D926A9]/20 text-[#D926A9]">{article.category}</Badge>
            <span className="text-xs text-[#B59ED1]">{article.published_at && new Date(article.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl mb-4" data-testid="article-title">{article.title}</h1>
          <p className="text-[#B59ED1] text-lg">{article.excerpt}</p>
        </div>

        {article.image_url && (
          <div className="rounded-2xl overflow-hidden mb-8 border border-white/10">
            <img src={article.image_url.startsWith('/') ? `${BACKEND_URL}${article.image_url}` : article.image_url} alt={article.title} className="w-full h-auto" />
          </div>
        )}

        {/* Keywords */}
        {article.keywords?.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-8">
            {article.keywords.map(k => (
              <Badge key={k} className="bg-[#3A115E] text-[#E2D5F8] text-xs">{k}</Badge>
            ))}
          </div>
        )}

        {/* Article Content (Markdown rendered as HTML) */}
        <div className="prose prose-invert max-w-none blog-content" data-testid="article-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(article.content)) }} />

        {/* CTA */}
        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-[#D926A9]/20 to-[#3A115E]/20 border border-[#D926A9]/20 text-center">
          <h3 className="font-serif text-xl mb-3">Envie d'aller plus loin&nbsp;?</h3>
          <p className="text-[#B59ED1] text-sm mb-4">Découvre mes consultations de voyance, soins énergétiques et ateliers à Montélimar et à distance.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/services')} className="gradient-primary">Mes prestations</Button>
            <Button onClick={() => navigate('/booking')} variant="outline" className="border-[#D926A9]/40 text-[#D926A9]">Prendre rendez-vous</Button>
          </div>
        </div>

        {/* Related articles - internal SEO maillage */}
        <RelatedArticles category={article.category} currentSlug={article.slug} />
      </div>
    </main>
  );
}

function RelatedArticles({ category, currentSlug }) {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!category) return;
    let mounted = true;
    axios.get(`${API}/blog/articles`, { params: { category, limit: 6 } })
      .then(res => {
        if (!mounted) return;
        const filtered = (res.data.articles || []).filter(a => a.slug !== currentSlug).slice(0, 3);
        setItems(filtered);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [category, currentSlug]);

  if (items.length === 0) return null;

  return (
    <section className="mt-16 pt-12 border-t border-white/10" data-testid="related-articles">
      <h2 className="font-serif text-2xl mb-2 text-white">À lire aussi</h2>
      <p className="text-sm text-[#B59ED1] mb-8">Sur le même thème : {category}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(a => (
          <article
            key={a.slug}
            onClick={() => navigate(`/blog/${a.slug}`)}
            className="group bg-[#261B3B] rounded-xl overflow-hidden border border-white/10 hover:border-[#D926A9]/50 transition-all duration-300 cursor-pointer"
            data-testid={`related-article-${a.slug}`}
          >
            {a.image_url && (
              <div className="aspect-video overflow-hidden bg-[#1B132B]">
                <img
                  src={a.image_url.startsWith('/') ? `${BACKEND_URL}${a.image_url}` : a.image_url}
                  alt={a.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            )}
            <div className="p-4">
              <Badge className="bg-[#D926A9]/20 text-[#D926A9] text-[10px] mb-2">{a.category}</Badge>
              <h3 className="font-serif text-base text-[#F7F3FC] group-hover:text-[#00E5FF] transition-colors line-clamp-2 mb-2">
                {a.title}
              </h3>
              <p className="text-xs text-[#B59ED1] line-clamp-2">{a.excerpt}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function renderMarkdown(md) {
  if (!md) return '';
  return md
    .replace(/### (.*?)(\n|$)/g, '<h3 class="font-serif text-xl mt-8 mb-3 text-white">$1</h3>')
    .replace(/## (.*?)(\n|$)/g, '<h2 class="font-serif text-2xl mt-10 mb-4 text-white">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n\n/g, '</p><p class="text-[#E2D5F8] leading-relaxed mb-4">')
    .replace(/\n/g, '<br/>')
    .replace(/^/, '<p class="text-[#E2D5F8] leading-relaxed mb-4">')
    .replace(/$/, '</p>');
}


// ============== EVENTS PAGE ==============

// Affiche toutes les dates prévues d'un événement sous forme de pastilles empilées
// (au lieu d'une seule date + "+N"), pour que les visiteurs voient tout d'un coup d'œil.
function EventDatePills({ dates, max = 4 }) {
  if (!dates || dates.length === 0) return null;
  const shown = dates.slice(0, max);
  const extra = dates.length - shown.length;
  return (
    <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1 max-w-[62%]" data-testid="event-date-pills">
      {shown.map((d, i) => {
        const dt = new Date(d.date + 'T00:00:00');
        const label = dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
        return (
          <span key={i} className="px-2.5 py-1 rounded-lg bg-black/65 backdrop-blur-sm border border-white/15 text-white text-[11px] font-medium whitespace-nowrap leading-none flex items-center gap-1">
            <Calendar className="w-3 h-3 text-[#00E5FF] shrink-0" />
            {label}{d.time && <span className="text-[#B59ED1]">· {d.time}</span>}
          </span>
        );
      })}
      {extra > 0 && (
        <span className="px-2.5 py-1 rounded-lg bg-[#D926A9]/80 backdrop-blur-sm text-white text-[11px] font-medium leading-none">
          +{extra} autre{extra > 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}


function EventsPage() {
  const { slug } = useParams();
  const [eventTypes, setEventTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentEvent, setPaymentEvent] = useState(null); // event en attente de paiement
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [eventPromo, setEventPromo] = useState(null); // code promo applique au paiement event
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesRes, eventsRes] = await Promise.all([
          axios.get(`${API}/event-types`),
          axios.get(`${API}/events?upcoming_only=true`)
        ]);
        setEventTypes(typesRes.data);
        setEvents(eventsRes.data);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Ouvre automatiquement la modale si l'URL contient un slug (/evenements/:slug)
  useEffect(() => {
    if (!slug || eventTypes.length === 0) return;
    const match = eventTypes.find(t => t.slug === slug);
    if (match) {
      setSelectedType(match);
    } else {
      toast.error("Événement introuvable");
      navigate('/evenements', { replace: true });
    }
  }, [slug, eventTypes, navigate]);

  // Synchronise l'URL avec la modale (back nav et lien partageable)
  const openTypeModal = (type) => {
    setSelectedType(type);
    if (type?.slug) navigate(`/evenements/${type.slug}`, { replace: false });
  };
  const closeTypeModal = () => {
    setSelectedType(null);
    if (slug) navigate('/evenements', { replace: false });
  };

  const handleRegister = async (event) => {
    if (!user) {
      toast.info("Connecte-toi pour t'inscrire");
      login();
      return;
    }
    if (!user.phone || !user.phone.trim()) {
      toast.error("Téléphone manquant. Ajoute-le depuis ton profil pour t'inscrire.", { duration: 6000 });
      navigate('/dashboard');
      return;
    }
    // Nouveau modèle "pay-first" : pour les events payants, on ouvre directement le modal Square
    // SANS créer de pending_payment. La registration ne sera créée qu'après charge Square OK.
    // ⚠️ Le prix vit sur event_type (template), pas sur l'event (instance). On lit donc en cascade.
    const price = Number(
      event.price ?? event.event_type?.price ?? selectedType?.price ?? 0
    ) || 0;
    if (price > 0) {
      setPaymentEvent({ ...event, amount: price, price });
      return;
    }
    // Event gratuit : registration directe
    try {
      await axios.post(`${API}/events/${event.event_id}/register`, {}, { withCredentials: true });
      toast.success("Inscription confirmée !");
      const eventsRes = await axios.get(`${API}/events?upcoming_only=true`);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    }
  };

  const handlePaymentSuccess = async (sourceId, verificationToken) => {
    if (!paymentEvent) return;
    setPaymentProcessing(true);
    try {
      // Nouveau modèle : on POST /register avec source_id → backend charge Square + crée registration atomique
      const res = await axios.post(
        `${API}/events/${paymentEvent.event_id}/register`,
        {
          source_id: sourceId,
          verification_token: verificationToken,
          promo_code: eventPromo?.code || null,
        },
        { withCredentials: true }
      );
      if (res.data?.success && res.data?.paid) {
        toast.success("Paiement réussi — inscription confirmée !");
        setPaymentEvent(null);
        setEventPromo(null);
        const eventsRes = await axios.get(`${API}/events?upcoming_only=true`);
        setEvents(eventsRes.data);
      } else {
        toast.error("Paiement non finalisé. Réessaye ou contacte-nous.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Paiement refusé");
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handlePaymentCancel = async () => {
    // Pay-first : aucune registration créée tant que le paiement n'a pas réussi.
    // L'utilisateur ferme simplement le modal — rien à nettoyer côté backend.
    setPaymentEvent(null);
    setEventPromo(null);
  };

  const handleUnregister = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/register`, { withCredentials: true });
      toast.success("Désinscription effectuée");
      const eventsRes = await axios.get(`${API}/events?upcoming_only=true`);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error("Erreur lors de la désinscription");
    }
  };

  const getUpcomingForType = (typeId) => events.filter(e => e.event_type_id === typeId);

  return (
    <main className="pt-24 min-h-screen">
      <Seo
        path="/evenements"
        title="Ateliers & Événements spirituels à Montélimar"
        description="Participez aux ateliers de méditation, Pranic Healing, développement spirituel et cercles énergétiques organisés par Bilan 2 Vie à Montélimar (Drôme)."
        keywords="atelier spirituel Montélimar, cercle méditation Drôme, atelier Pranic Healing, événement ésotérique"
      />
      <section className="py-16 bg-[#1B132B]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <SectionDivider icon={Sparkles} label="Rejoignez-nous" color="#D926A9" />
            <h1 className="font-serif text-5xl md:text-6xl mb-6">Événements & Ateliers</h1>
            <p className="text-[#B59ED1] max-w-2xl mx-auto">
              Offre-toi des rendez-vous inspirants mêlant transmission, pratique, partage et élévation, dans une atmosphère douce, intime et profondément ressourçante.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="h-80 rounded-2xl skeleton"></div>)}
            </div>
          ) : (
            <>
              {/* Soirées */}
              {(() => {
                const soirees = eventTypes.filter(t => t.category === 'Soirée');
                const ateliers = eventTypes.filter(t => t.category === 'Atelier');
                return (
                  <>
                    {soirees.length > 0 && (
                      <div className="mb-12">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="h-px flex-1 bg-gradient-to-r from-[#D926A9]/50 to-transparent"></div>
                          <Sparkles className="w-4 h-4 text-[#D926A9]" />
                          <span className="text-[#D926A9] text-sm uppercase tracking-[0.15em] font-medium">Soirées Mystiques</span>
                          <div className="h-px flex-1 bg-gradient-to-l from-[#D926A9]/50 to-transparent"></div>
                        </div>
                        <p className="text-[#B59ED1] text-center text-sm mb-6">Viens activer ta magie intérieure : ancrage, purification, tirage d'oracles, rituel de lâcher-prise.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          {soirees.map((type, idx) => {
                            const upcoming = getUpcomingForType(type.event_type_id);
                            return (
                              <motion.div
                                key={type.event_type_id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="group cursor-pointer rounded-2xl overflow-hidden border border-[#D926A9]/30 hover:border-[#D926A9]/60 transition-all"
                                onClick={() => openTypeModal(type)}
                                data-testid={`event-card-${type.event_type_id}`}
                              >
                                <div className="relative h-52 md:h-64 overflow-hidden">
                                  <img src={type.image_url} alt={type.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
                                  {/* Vignette: a bit of dark on top + stronger gradient at the bottom for the subtitle */}
                                  <div className="absolute inset-0 bg-gradient-to-b from-[#1B132B]/70 via-transparent to-[#1B132B]/80" />
                                  <Badge className="absolute top-3 left-3 bg-[#D926A9] text-white text-xs backdrop-blur-sm z-10">{type.category}</Badge>
                                  <EventDatePills dates={upcoming} />
                                  {/* Title + subtitle centered in the LOWER HALF of the card */}
                                  <div className="absolute top-1/2 inset-x-0 bottom-0 flex flex-col items-center justify-center px-4 gap-1">
                                    <h2 className="font-mystic text-2xl md:text-4xl text-white text-center drop-shadow-[0_3px_8px_rgba(0,0,0,0.85)] tracking-wide leading-tight">{type.name}</h2>
                                    {upcoming[0]?.subtitle && (
                                      <p className="font-mystic text-base md:text-2xl text-[#00E5FF] text-center drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)] leading-tight">
                                        {upcoming[0].subtitle}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="p-5 bg-[#261B3B] flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <span className="text-[#00E5FF] font-semibold text-lg">{type.price} €</span>
                                    <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs"><Clock className="w-3 h-3 mr-1" />{type.duration_minutes} min</Badge>
                                  </div>
                                  <span className="text-[#D926A9] text-sm font-medium group-hover:translate-x-1 transition-transform">Découvrir →</span>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {ateliers.length > 0 && (
                      <div>
                        <div className="flex items-center gap-3 mb-6">
                          <div className="h-px flex-1 bg-gradient-to-r from-[#00E5FF]/30 to-transparent"></div>
                          <Star className="w-4 h-4 text-[#00E5FF]" />
                          <span className="text-[#00E5FF] text-sm uppercase tracking-[0.15em] font-medium">Ateliers</span>
                          <div className="h-px flex-1 bg-gradient-to-l from-[#00E5FF]/30 to-transparent"></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {ateliers.map((type, idx) => {
                            const upcoming = getUpcomingForType(type.event_type_id);
                            return (
                              <motion.div
                                key={type.event_type_id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="group cursor-pointer rounded-2xl overflow-hidden border border-white/10 hover:border-[#00E5FF]/30 transition-all"
                                onClick={() => openTypeModal(type)}
                                data-testid={`event-card-${type.event_type_id}`}
                              >
                                <div className="relative h-48 overflow-hidden">
                                  <img src={type.image_url} alt={type.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; }} />
                                  <div className="absolute inset-0 bg-gradient-to-t from-[#261B3B] via-transparent to-transparent" />
                                  <Badge className="absolute top-3 left-3 bg-[#3A115E]/80 text-[#E2D5F8] text-xs backdrop-blur-sm">{type.category}</Badge>
                                  <EventDatePills dates={upcoming} />
                                  <div className="absolute bottom-3 left-3 right-3">
                                    <h3 className="font-serif text-xl text-white drop-shadow-lg">{type.name}</h3>
                                    {upcoming[0]?.subtitle && (
                                      <p className="text-[#00E5FF] text-xs italic drop-shadow mt-0.5">{upcoming[0].subtitle}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="p-4 bg-[#261B3B] flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[#00E5FF] font-semibold">{type.price} €</span>
                                    <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs"><Clock className="w-3 h-3 mr-1" />{type.duration_minutes} min</Badge>
                                  </div>
                                  <span className="text-[#B59ED1] text-sm group-hover:text-[#00E5FF] transition-colors">En savoir plus →</span>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </section>

      {/* Event Type Detail Modal */}
      <AnimatePresence>
        {selectedType && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeTypeModal} />
            <motion.div
              className="relative bg-[#261B3B] rounded-2xl border border-white/15 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              data-testid="event-modal"
            >
              {selectedType.image_url && (
                <div className="relative h-56 overflow-hidden rounded-t-2xl">
                  <img src={selectedType.image_url} alt={selectedType.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#261B3B] via-transparent to-transparent" />
                </div>
              )}
              <button
                onClick={closeTypeModal}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors z-10"
                data-testid="event-modal-close"
              >
                ×
              </button>
              <div className="p-8">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs">{selectedType.category}</Badge>
                  <Badge className="bg-[#D926A9]/10 text-[#D926A9] text-xs border border-[#D926A9]/20">
                    <MapPin className="w-3 h-3 mr-1" />En boutique uniquement
                  </Badge>
                  <Badge className="bg-[#3A115E] text-[#E2D5F8] text-xs">Min. {selectedType.min_participants} pers.</Badge>
                </div>
                <h2 className="font-serif text-3xl text-[#F7F3FC] mb-4">{selectedType.name}</h2>
                <p className="text-[#E2D5F8] leading-relaxed mb-6 whitespace-pre-line">{selectedType.description}</p>

                {/* Upcoming dates for this type */}
                {(() => {
                  const upcoming = getUpcomingForType(selectedType.event_type_id);
                  if (upcoming.length > 0) {
                    return (
                      <div className="mb-6">
                        <h3 className="text-sm uppercase tracking-wider text-[#B59ED1] mb-3">Prochaines dates</h3>
                        <div className="space-y-2">
                          {upcoming.map(evt => {
                            const spotsLeft = evt.max_participants - (evt.registrations?.length || 0);
                            const isRegistered = user && evt.registrations?.some(r => r.user_id === user.user_id);
                            const dateObj = new Date(evt.date + 'T00:00:00');
                            const dateStr = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
                            return (
                              <div key={evt.event_id} className="flex items-center justify-between py-3 px-4 rounded-lg bg-[#1B132B] border border-white/5">
                                <div>
                                  <span className="text-[#E2D5F8] capitalize">{dateStr}</span>
                                  <span className="text-[#B59ED1] text-sm ml-2">à {evt.time}</span>
                                  <span className={`text-xs ml-3 ${spotsLeft <= 2 ? 'text-red-400' : 'text-green-400'}`}>
                                    {spotsLeft} place{spotsLeft > 1 ? 's' : ''} restante{spotsLeft > 1 ? 's' : ''}
                                  </span>
                                  {evt.subtitle && (
                                    <p className="text-[#D926A9] text-xs italic mt-1">{evt.subtitle}</p>
                                  )}
                                </div>
                                {isRegistered ? (
                                  <Button size="sm" variant="outline" className="border-red-400/40 text-red-400 hover:bg-red-400/10" onClick={(e) => { e.stopPropagation(); handleUnregister(evt.event_id); }} data-testid={`unregister-${evt.event_id}`}>
                                    Se désinscrire
                                  </Button>
                                ) : spotsLeft > 0 ? (
                                  <Button size="sm" className="gradient-primary" onClick={(e) => { e.stopPropagation(); handleRegister(evt); }} data-testid={`register-${evt.event_id}`}>
                                    S'inscrire
                                  </Button>
                                ) : (
                                  <Badge className="bg-red-500/20 text-red-400 border border-red-400/20">Complet</Badge>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="mb-6 p-4 rounded-lg bg-[#1B132B] border border-white/5 text-center">
                      <p className="text-[#B59ED1] text-sm">Aucune date programmée pour le moment. Restez à l'écoute !</p>
                    </div>
                  );
                })()}

                <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-serif text-[#00E5FF]">{selectedType.price} €</span>
                    <Badge className="bg-[#3A115E] text-[#E2D5F8]">
                      <Clock className="w-3 h-3 mr-1" />{selectedType.duration_minutes} min
                    </Badge>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de paiement pour l'inscription a un evenement */}
      <Dialog open={!!paymentEvent} onOpenChange={(open) => { if (!open && !paymentProcessing) handlePaymentCancel(); }}>
        <DialogContent className="bg-[#261B3B] border-white/15 text-[#E2D5F8] max-w-md w-[calc(100vw-1.5rem)] p-0 overflow-hidden" data-testid="event-payment-modal">
          <DialogHeader className="p-5 pb-3 border-b border-white/10">
            <DialogTitle className="font-serif text-lg sm:text-xl text-[#F7F3FC] leading-tight text-left">
              Finalise ton inscription
            </DialogTitle>
          </DialogHeader>
          {paymentEvent && (
            <div className="p-5 space-y-4">
              <div className="rounded-xl bg-[#1B132B] border border-white/10 p-4">
                <p className="text-xs uppercase tracking-wider text-[#B59ED1] mb-1">Événement</p>
                <p className="font-serif text-lg text-[#F7F3FC]">{paymentEvent.name || paymentEvent.title}</p>
                {paymentEvent.subtitle && (
                  <p className="font-mystic text-sm text-[#00E5FF] mt-0.5">{paymentEvent.subtitle}</p>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                  <div className="text-xs text-[#B59ED1]">
                    <Calendar className="w-3 h-3 inline mr-1 text-[#00E5FF]" />
                    {(() => {
                      try {
                        const d = new Date(paymentEvent.date + 'T00:00:00');
                        return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                      } catch { return paymentEvent.date; }
                    })()}
                    {paymentEvent.time && <span> · {paymentEvent.time}</span>}
                  </div>
                  <p className="text-xl font-serif text-[#D926A9]">{Number(paymentEvent.amount || 0).toFixed(2)} €</p>
                </div>
              </div>
              {/* Code promo */}
              <PromoCodeInput
                subtotal={Number(paymentEvent.amount || 0)}
                surface="events"
                applied={eventPromo}
                onApplied={(p) => { setEventPromo(p); toast.success(`Code ${p.code} appliqué : −${p.discount_amount.toFixed(2)} €`); }}
                onCleared={() => setEventPromo(null)}
                testIdPrefix="event-promo"
              />
              {/* Total final */}
              {eventPromo && (
                <div className="flex items-center justify-between rounded-lg bg-[#1B132B] border border-white/10 px-3 py-2 text-sm" data-testid="event-final-total">
                  <span className="text-[#B59ED1]">Total à payer</span>
                  <span className="text-lg font-serif text-[#00E5FF]">
                    {Math.max(0, Number(paymentEvent.amount || 0) - Number(eventPromo.discount_amount || 0)).toFixed(2)} €
                  </span>
                </div>
              )}
              <SquarePaymentForm
                amount={Math.max(0, Number(paymentEvent.amount || 0) - Number(eventPromo?.discount_amount || 0))}
                onSuccess={handlePaymentSuccess}
                disabled={paymentProcessing}
                buttonText="Payer"
              />
              <button
                onClick={handlePaymentCancel}
                disabled={paymentProcessing}
                className="w-full text-xs text-[#B59ED1] hover:text-white py-2 disabled:opacity-50"
                data-testid="event-payment-cancel"
              >
                Annuler l'inscription
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Aide : appeler en cas de difficulté pour s'inscrire en ligne */}
      <p className="pb-16 px-6 text-center text-xs text-[#B59ED1] max-w-md mx-auto leading-relaxed" data-testid="events-help-call">
        Une difficulté pour t'inscrire en ligne ?{' '}
        <a href="tel:+33768489056" className="text-[#00E5FF] hover:underline font-medium whitespace-nowrap">
          Appelle-moi au 07&nbsp;68&nbsp;48&nbsp;90&nbsp;56
        </a>{' '}et on réserve ta place ensemble.
      </p>
    </main>
  );
}


// ============== BOUTIQUE PAGE ==============

// Catégories principales regroupées
// Catégories "principales" pour le tri/label personnalisé.
// Les catégories réellement affichées sont calculées dynamiquement
// depuis les produits actifs en DB (cf BoutiquePage).
const MAIN_CATEGORY_LABELS = {
  "Pierres & Cristaux": "Pierres & Cristaux",
  "Bijoux Spirituels": "Bijoux",
  "Encens & Purification": "Encens & Purification",
  "Bougies & Rituels": "Bougies & Rituels",
  "Oracles & Tarots": "Oracles & Tarots",
  "Divination": "Divination",
  "Huiles & Essences": "Huiles & Aromathérapie",
  "Bols & Sons": "Bols & Instruments",
  "Livres & Guides": "Livres",
  "Méditation & Bien-être": "Méditation & Bien-être",
  "Décoration Spirituelle": "Décoration",
  "Thés & Boissons": "Thés & Boissons",
  "Coffrets & Cadeaux": "Coffrets & Cadeaux",
};
// Conservation pour les zones du site qui s'attendent à un tableau (page d'accueil)
const MAIN_CATEGORIES = Object.entries(MAIN_CATEGORY_LABELS).map(([key, label]) => ({ key, label }));

// Cache mémoire global pour la boutique : évite le flash "vide" lors d'une nav
// arrière depuis une fiche produit. Le cache est invalidé après 5 min.
const PRODUCTS_CACHE = { data: null, fetchedAt: 0 };
const GROUPS_CACHE = { data: null, fetchedAt: 0 };
const PRODUCTS_CACHE_TTL_MS = 5 * 60 * 1000;

function BoutiquePage() {
  // Mémoire navigation : restaure l'état de la dernière visite si retour-arrière depuis fiche produit
  const SHOP_STATE_KEY = "boutique_nav_state_v1";
  const SHOP_SCROLL_KEY = "boutique_nav_scroll_v1";
  const restored = (() => {
    try {
      const raw = sessionStorage.getItem(SHOP_STATE_KEY);
      if (!raw) return {};
      const data = JSON.parse(raw);
      // Expire après 30 min pour éviter de garder un état stale
      if (Date.now() - (data.savedAt || 0) > 30 * 60 * 1000) return {};
      return data;
    } catch { return {}; }
  })();

  const [products, setProducts] = useState(() => {
    if (PRODUCTS_CACHE.data && Date.now() - PRODUCTS_CACHE.fetchedAt < PRODUCTS_CACHE_TTL_MS) {
      return PRODUCTS_CACHE.data;
    }
    return [];
  });
  const [allProducts, setAllProducts] = useState(() => {
    if (PRODUCTS_CACHE.data && Date.now() - PRODUCTS_CACHE.fetchedAt < PRODUCTS_CACHE_TTL_MS) {
      return PRODUCTS_CACHE.data;
    }
    return [];
  });
  const [groups, setGroups] = useState(() => GROUPS_CACHE.data || []);
  const [searchParams, setSearchParams] = useSearchParams();
  // Group sélectionné = catégorie principale éditoriale (lithothérapie, etc.)
  const [selectedGroupId, setSelectedGroupIdRaw] = useState(searchParams.get('group') || null);
  // Sous-catégorie Square (filtre fin) au sein d'un group
  const [selectedSquareCat, setSelectedSquareCatRaw] = useState(searchParams.get('sub') || null);
  const [searchQuery, setSearchQuery] = useState(restored.searchQuery || "");
  const [sortBy, setSortBy] = useState(restored.sortBy || "featured"); // featured | recent | price-asc | price-desc | name-asc | name-desc
  const [loading, setLoading] = useState(() => !PRODUCTS_CACHE.data);
  const [displayCount, setDisplayCount] = useState(restored.displayCount || 24);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Sync state to URL pour partage + back-nav
  const setSelectedGroupId = (gid) => {
    setSelectedGroupIdRaw(gid);
    setSelectedSquareCatRaw(null); // reset sub-filter when changing group
    const params = {};
    if (gid) params.group = gid;
    setSearchParams(params, { replace: true });
  };
  const setSelectedSquareCat = (sq) => {
    setSelectedSquareCatRaw(sq);
    const params = {};
    if (selectedGroupId) params.group = selectedGroupId;
    if (sq) params.sub = sq;
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      const productsCacheFresh = PRODUCTS_CACHE.data
        && Date.now() - PRODUCTS_CACHE.fetchedAt < PRODUCTS_CACHE_TTL_MS;
      const groupsCacheFresh = GROUPS_CACHE.data
        && Date.now() - GROUPS_CACHE.fetchedAt < PRODUCTS_CACHE_TTL_MS;
      if (productsCacheFresh && groupsCacheFresh) {
        setLoading(false);
        return;
      }
      try {
        const [productsRes, groupsRes] = await Promise.all([
          productsCacheFresh
            ? Promise.resolve({ data: PRODUCTS_CACHE.data })
            : axios.get(`${API}/products`),
          groupsCacheFresh
            ? Promise.resolve({ data: { groups: GROUPS_CACHE.data } })
            : axios.get(`${API}/main-categories`),
        ]);
        if (cancelled) return;
        if (!productsCacheFresh) {
          PRODUCTS_CACHE.data = productsRes.data;
          PRODUCTS_CACHE.fetchedAt = Date.now();
          setAllProducts(productsRes.data);
        }
        if (!groupsCacheFresh) {
          GROUPS_CACHE.data = groupsRes.data.groups || [];
          GROUPS_CACHE.fetchedAt = Date.now();
          setGroups(groupsRes.data.groups || []);
        }
      } catch (error) {
        if (!cancelled) toast.error("Erreur lors du chargement des produits");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  // Persiste l'état dans sessionStorage à chaque changement (sortBy, query, displayCount)
  useEffect(() => {
    try {
      sessionStorage.setItem(SHOP_STATE_KEY, JSON.stringify({
        sortBy, searchQuery, displayCount,
        savedAt: Date.now(),
      }));
    } catch { /* quota / private mode */ }
  }, [sortBy, searchQuery, displayCount]);

  // Restaure la position de scroll APRÈS que les produits soient chargés (sinon la page est plus petite que le scroll cible)
  useEffect(() => {
    if (loading) return;
    if (allProducts.length === 0) return;
    let y;
    try {
      y = parseInt(sessionStorage.getItem(SHOP_SCROLL_KEY) || "0", 10);
    } catch { return; }
    if (!y || y <= 100) return;

    let attempts = 0;
    const maxAttempts = 50; // ~5s total
    const tryScroll = () => {
      attempts += 1;
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      // Si la page est encore trop courte, on retente
      if (maxScroll + 50 < y && attempts < maxAttempts) {
        setTimeout(tryScroll, 100);
        return;
      }
      window.scrollTo(0, y);
      // Re-essai juste après pour les ré-hydratations tardives (images lazy)
      requestAnimationFrame(() => {
        const cur = window.scrollY || 0;
        if (Math.abs(cur - y) > 50) {
          window.scrollTo(0, y);
        }
      });
      try { sessionStorage.removeItem(SHOP_SCROLL_KEY); } catch { /* ignore */ }
    };
    const t = setTimeout(tryScroll, 50);
    return () => clearTimeout(t);
  }, [loading, allProducts.length]);

  // Navigate vers une fiche produit en sauvegardant la position de scroll
  const navigateToProduct = (productId) => {
    try {
      sessionStorage.setItem(SHOP_SCROLL_KEY, String(window.scrollY || 0));
    } catch { /* ignore */ }
    navigate(`/boutique/${productId}`);
  };

  // Filtrer les produits par groupe principal + sous-catégorie + recherche
  useEffect(() => {
    let filtered = [...allProducts];

    // 1) Filtre par GROUPE principal (catégorie éditoriale)
    if (selectedGroupId) {
      const group = groups.find(g => g.category_id === selectedGroupId);
      if (group) {
        const groupSqCats = new Set(group.square_categories || []);
        filtered = filtered.filter(p => groupSqCats.has(p.category));
      }
    }

    // 2) Sous-filtre : une catégorie Square spécifique du groupe
    if (selectedSquareCat) {
      filtered = filtered.filter(p => p.category === selectedSquareCat);
    }

    if (searchQuery.trim()) {
      // Normalise : minuscules + supprime les accents (NFD → strip diacritics)
      // Ainsi "encens" trouve "Encens", "Encéns" et "Énergie" ↔ "energie"
      const normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const query = normalize(searchQuery);
      filtered = filtered.filter(p =>
        normalize(p.name).includes(query) ||
        normalize(p.category).includes(query) ||
        (p.description && normalize(p.description).includes(query))
      );
    }

    // Masque les produits en rupture de stock (n'affiche que les disponibles)
    filtered = filtered.filter(p => (p.stock || 0) > 0);

    // Tri utilisateur (override le tri "featured first" par défaut)
    if (sortBy === "recent") {
      filtered.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    } else if (sortBy === "price-asc") {
      filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortBy === "price-desc") {
      filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
    } else if (sortBy === "name-asc" || sortBy === "name-desc") {
      const dir = sortBy === "name-asc" ? 1 : -1;
      filtered.sort((a, b) => dir * (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
    } else if (!selectedGroupId && !selectedSquareCat) {
      // Mode "featured" (défaut) : coups de cœur en tête, uniquement sans filtre catégorie
      filtered = sortFeaturedFirst(filtered);
    }

    setProducts(filtered);
    setDisplayCount(24);
  }, [selectedGroupId, selectedSquareCat, searchQuery, sortBy, allProducts, groups]);

  const loadMore = () => setDisplayCount(prev => prev + 24);

  return (
    <main className="pt-24 min-h-screen">
      <Seo
        path="/boutique"
        title="Boutique ésotérique en ligne — Cristaux, encens, tarots, bougies"
        description="Boutique spirituelle en ligne avec livraison partout en France (Mondial Relay) ou retrait à Montélimar (Click & Collect). Cristaux, encens, tarots, bougies, bijoux ésotériques."
        keywords="boutique ésotérique, cristaux en ligne, encens naturel, tarot divinatoire, bougies rituelles, bijoux spirituels, livraison France"
      />
      <section className="py-16 bg-[#1B132B]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#00E5FF] text-sm uppercase tracking-[0.2em] mb-4 block">La Boutique</span>
            <h1 className="font-serif text-5xl md:text-6xl mb-6">Articles Spirituels</h1>
            <p className="text-[#B59ED1] max-w-2xl mx-auto mb-8">
              Découvre une sélection de {allProducts.filter(p => (p.stock || 0) > 0).length} articles ésotériques soigneusement choisis pour accompagner ta pratique spirituelle.
            </p>
            
            {/* Search Bar + Sort */}
            <div className="max-w-2xl mx-auto mb-8 flex flex-col sm:flex-row gap-3">
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#261B3B] border-white/20 text-white placeholder:text-[#B59ED1] flex-1"
                data-testid="search-input"
              />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-[#261B3B] border border-white/20 text-white text-sm rounded-md px-3 py-2 cursor-pointer hover:border-[#D926A9]/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D926A9]/50 min-w-[200px]"
                data-testid="sort-select"
              >
                <option value="featured">Coups de cœur d&apos;abord</option>
                <option value="recent">Nouveautés (+ récent)</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
                <option value="name-asc">Nom A → Z</option>
                <option value="name-desc">Nom Z → A</option>
              </select>
            </div>

            {/* CTA Carte Cadeau Square — placé HORS des catégories pour ne pas polluer le filtrage */}
            <motion.a
              href="https://app.squareup.com/gift/MLXZPE5BYDD43/order"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
              className="group inline-flex items-center gap-2 sm:gap-3 max-w-xl mx-auto mb-2 px-3 py-2 sm:px-6 sm:py-4 rounded-xl sm:rounded-2xl border border-[#D926A9]/40 bg-gradient-to-r from-[#D926A9]/15 via-[#3A115E]/30 to-[#00E5FF]/10 backdrop-blur-sm hover:border-[#D926A9] hover:shadow-[0_0_30px_rgba(217,38,169,0.35)] transition-all"
              data-testid="boutique-giftcard-cta"
            >
              <span className="flex-shrink-0 w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-[#D926A9] to-[#00E5FF] flex items-center justify-center shadow-lg">
                <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </span>
              <span className="text-left">
                <span className="block text-sm sm:text-lg font-serif text-white leading-tight">Offrir une carte cadeau</span>
                <span className="hidden sm:block text-[11px] sm:text-xs text-[#B59ED1] mt-0.5">
                  Montant au choix — le cadeau parfait pour les âmes en quête de lumière
                </span>
              </span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#00E5FF] ml-auto group-hover:translate-x-1 transition-transform" />
            </motion.a>
          </div>

          {/* Bandeau confiance — version compacte sur la boutique pour laisser plus de place aux produits */}
          <TrustStrip compact />

          {/* Pills de groupes principaux + sous-catégories Square */}
          <div className="-mx-6 px-6 sm:mx-0 sm:px-0 mb-6">
            <div className="flex sm:flex-wrap sm:justify-center gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-0 sm:overflow-visible no-scrollbar">
              {/* Pill "Tous" */}
              {(() => {
                const visibleProducts = allProducts.filter(p => (p.stock || 0) > 0);
                return (
                  <button
                    onClick={() => setSelectedGroupId(null)}
                    className={`category-pill shrink-0 ${!selectedGroupId ? 'active' : ''}`}
                    data-testid="group-all"
                  >
                    Tous ({visibleProducts.length})
                  </button>
                );
              })()}
              {/* Groupes principaux (filtrés sur ceux qui ont au moins 1 produit visible) */}
              {groups
                .filter(g => g.is_active !== false && (g.product_count || 0) > 0)
                .map(g => (
                  <button
                    key={g.category_id}
                    onClick={() => setSelectedGroupId(g.category_id)}
                    className={`category-pill shrink-0 ${selectedGroupId === g.category_id ? 'active' : ''}`}
                    data-testid={`group-${g.category_id}`}
                    title={g.description || g.label}
                  >
                    {g.emoji ? <span className="mr-1.5">{g.emoji}</span> : null}
                    {g.label} ({g.product_count})
                  </button>
                ))}
            </div>
          </div>

          {/* Sous-catégories Square du groupe sélectionné */}
          {selectedGroupId && (() => {
            const group = groups.find(g => g.category_id === selectedGroupId);
            if (!group || !group.square_categories || group.square_categories.length === 0) return null;
            // Compter les produits visibles par sq cat dans le groupe
            const counts = group.square_categories.reduce((acc, sq) => {
              acc[sq] = allProducts.filter(p => p.category === sq && (p.stock || 0) > 0).length;
              return acc;
            }, {});
            const visibleSubs = group.square_categories.filter(sq => counts[sq] > 0);
            if (visibleSubs.length <= 1) return null; // Inutile si une seule sous-cat
            return (
              <div className="-mx-6 px-6 sm:mx-0 sm:px-0 mb-10">
                <div className="flex sm:flex-wrap sm:justify-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 sm:pb-0 sm:overflow-visible no-scrollbar">
                  <button
                    onClick={() => setSelectedSquareCat(null)}
                    className={`subcat-pill shrink-0 ${!selectedSquareCat ? 'active' : ''}`}
                    data-testid="subcat-all"
                  >
                    Tout {group.label.toLowerCase()}
                  </button>
                  {visibleSubs.sort().map(sq => (
                    <button
                      key={sq}
                      onClick={() => setSelectedSquareCat(sq)}
                      className={`subcat-pill shrink-0 ${selectedSquareCat === sq ? 'active' : ''}`}
                      data-testid={`subcat-${sq}`}
                    >
                      {sq} ({counts[sq]})
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Results count */}
          {(selectedGroupId || searchQuery) && (() => {
            const group = selectedGroupId ? groups.find(g => g.category_id === selectedGroupId) : null;
            return (
              <p className="text-center text-[#B59ED1] mb-8">
                {products.length} résultat{products.length > 1 ? 's' : ''}
                {group && ` dans "${selectedSquareCat || group.label}"`}
                {searchQuery && ` pour "${searchQuery}"`}
              </p>
            );
          })()}

          {loading ? (
            <div className="product-grid">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="aspect-square rounded-2xl skeleton"></div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <Sparkles className="w-16 h-16 text-[#B59ED1] mx-auto mb-4" />
              <p className="text-[#B59ED1] text-lg">Aucun produit trouvé</p>
              <Button
                onClick={() => { setSelectedGroupId(null); setSearchQuery(""); }}
                variant="ghost"
                className="mt-4 text-[#00E5FF]"
              >
                Voir tous les produits
              </Button>
            </div>
          ) : (
            <>
              <div className="product-grid">
                <AnimatePresence mode="popLayout">
                  {products.slice(0, displayCount).map((product, index) => (
                    <motion.div
                      key={product.product_id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: Math.min(index * 0.03, 0.5) }}
                      className="product-card cursor-pointer group"
                      onClick={() => navigateToProduct(product.product_id)}
                      data-testid={`product-card-${product.product_id}`}
                    >
                      <div className="aspect-square overflow-hidden relative">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={(e) => { e.target.src = '/api/static/images/fallback_product.jpg'; }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#3A115E] to-[#261B3B] flex items-center justify-center">
                            <Sparkles className="w-12 h-12 text-[#D926A9]" />
                          </div>
                        )}
                        {(product.stock || 0) <= 0 && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-white font-medium">Rupture de stock</span>
                          </div>
                        )}
                        {product.has_variations && (
                          <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-[#D926A9]/90 text-white text-[10px] font-medium backdrop-blur-sm">
                            {product.variations?.length} déclinaisons
                          </div>
                        )}
                        {isFeatured(product) && (
                          <div className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#D926A9] to-[#9D4CDD] text-white text-[10px] font-medium uppercase tracking-wider shadow-lg flex items-center gap-1" data-testid={`featured-badge-${product.product_id}`}>
                            <Heart className="w-3 h-3 fill-white" />
                            Coup de cœur
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <Badge className="bg-[#3A115E] text-[#E2D5F8] mb-3 text-xs">{product.category}</Badge>
                        <h3 className="font-serif text-lg mb-2 line-clamp-2">
                          <Link to={`/boutique/${product.product_id}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#D926A9] transition-colors">{product.name}</Link>
                        </h3>
                        <p className="text-[#B59ED1] text-sm mb-4 line-clamp-2">{stripMarkdown(product.short_description)}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-serif text-[#D926A9]">{product.price?.toFixed(2) || '0.00'} €</span>
                          <Button 
                            onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                            size="sm" 
                            className="gradient-primary"
                            disabled={(product.stock || 0) <= 0}
                            data-testid={`boutique-add-${product.product_id}`}
                          >
                            <ShoppingCart className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              
              {/* Load More Button */}
              {displayCount < products.length && (
                <div className="text-center mt-12">
                  <Button 
                    onClick={loadMore}
                    variant="outline"
                    className="border-[#00E5FF]/40 text-[#00E5FF] px-8"
                    data-testid="load-more"
                  >
                    Voir plus ({products.length - displayCount} restants)
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}

// ============== BOOKING PAGE ==============

// ============== Page : Demande de devis (services "sur devis") ==============
function QuotePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(location.state?.service || null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    address: '',
    city: '',
    postal_code: '',
    surface: '',
    message: '',
  });

  useEffect(() => {
    axios.get(`${API}/services`).then(r => {
      const list = (r.data || []).filter(s => s.is_quote);
      setServices(list);
      // Auto-selectionne s'il n'y a qu'un seul service "sur devis"
      if (list.length === 1) setSelectedService(prev => prev || list[0]);
    });
  }, []);

  useEffect(() => {
    setForm(f => ({ ...f, customer_name: user?.name || f.customer_name, customer_email: user?.email || f.customer_email }));
  }, [user]);

  const handleSubmit = async () => {
    if (!selectedService) { toast.error("Choisis une prestation"); return; }
    if (!form.customer_name?.trim() || !form.customer_email?.trim() || !form.message?.trim()) {
      toast.error("Nom, email et message sont obligatoires");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        service_id: selectedService.service_id,
        ...form,
      });
      setSubmitted(true);
      toast.success("Demande de devis envoyée !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main className="pt-24 min-h-screen bg-[#1B132B]">
        <Seo path="/devis" title="Demande de devis envoyée" description="Demande de devis pour un service de purification de lieux à domicile. Bilan 2 Vie revient vers toi sous 48h." />
        <div className="max-w-xl mx-auto px-6 py-16 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-20 h-20 rounded-full bg-[#D926A9]/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-[#D926A9]" />
          </motion.div>
          <h1 className="font-serif text-4xl mb-4">Demande envoyée !</h1>
          <p className="text-[#B59ED1] mb-8 leading-relaxed">
            Merci pour ta confiance. Je reviens vers toi sous <strong className="text-white">48h ouvrées</strong> avec un devis personnalisé pour <strong className="text-[#D926A9]">{selectedService?.name}</strong>.
          </p>
          <p className="text-sm text-[#B59ED1] mb-8">Un email de confirmation t&apos;a été envoyé à <span className="text-[#00E5FF]">{form.customer_email}</span>.</p>
          <Button onClick={() => navigate('/')} className="gradient-primary" data-testid="quote-back-home">Retour à l&apos;accueil</Button>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 min-h-screen bg-[#1B132B]">
      <Seo path="/devis" title="Demande de devis — Purification de lieux à domicile" description="Demande un devis personnalisé pour la purification énergétique de ton lieu de vie ou de travail. Bilan 2 Vie te répond sous 48h." keywords="devis purification lieu, purification énergétique maison, nettoyage énergétique domicile" />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#B59ED1] hover:text-white mb-6 text-sm transition-colors" data-testid="back-button">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="text-center mb-10">
          <span className="text-[#00E5FF] text-sm uppercase tracking-[0.2em] mb-3 block">Devis sur mesure</span>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Demande de devis</h1>
          <p className="text-[#B59ED1] max-w-xl mx-auto">
            Remplis ce formulaire, je reviens vers toi sous 48h ouvrées avec un devis adapté à ta situation.
          </p>
        </div>

        <div className="space-y-5">
          {/* Selection service */}
          <Card className="bg-[#261B3B] border-white/10">
            <CardContent className="pt-5">
              <Label className="text-xs text-[#B59ED1] mb-2 block">Prestation *</Label>
              {services.length > 1 ? (
                <Select value={selectedService?.service_id || ''} onValueChange={(v) => setSelectedService(services.find(s => s.service_id === v))}>
                  <SelectTrigger className="bg-[#1B132B] border-white/10" data-testid="quote-service"><SelectValue placeholder="Choisis une prestation" /></SelectTrigger>
                  <SelectContent className="bg-[#261B3B] border-white/10">
                    {services.map(s => (
                      <SelectItem key={s.service_id} value={s.service_id} className="text-white hover:bg-[#3A115E] focus:bg-[#3A115E] focus:text-white">
                        {s.name}{s.quote_starting_price ? ` — Dès ${s.quote_starting_price} €` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : selectedService ? (
                <div className="p-3 bg-[#1B132B] rounded-lg flex items-center justify-between">
                  <span className="text-white">{selectedService.name}</span>
                  {selectedService.quote_starting_price && <span className="text-[#00E5FF] text-sm">Dès {selectedService.quote_starting_price} €</span>}
                </div>
              ) : <p className="text-sm text-[#B59ED1]">Aucune prestation sur devis disponible.</p>}
            </CardContent>
          </Card>

          {/* Coordonnees */}
          <Card className="bg-[#261B3B] border-white/10">
            <CardContent className="pt-5">
              <h3 className="font-serif text-lg mb-4">Tes coordonnées</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#B59ED1]">Nom complet *</Label>
                  <Input value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} placeholder="Nom Prénom" className="mt-1 bg-[#1B132B] border-white/10" data-testid="quote-name" />
                </div>
                <div>
                  <Label className="text-xs text-[#B59ED1]">Email *</Label>
                  <Input type="email" value={form.customer_email} onChange={e => setForm({...form, customer_email: e.target.value})} placeholder="ton@email.com" className="mt-1 bg-[#1B132B] border-white/10" data-testid="quote-email" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-[#B59ED1]">Téléphone</Label>
                  <Input value={form.customer_phone} onChange={e => setForm({...form, customer_phone: e.target.value})} placeholder="06 00 00 00 00" className="mt-1 bg-[#1B132B] border-white/10" data-testid="quote-phone" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lieu */}
          <Card className="bg-[#261B3B] border-white/10">
            <CardContent className="pt-5">
              <h3 className="font-serif text-lg mb-1">Le lieu à purifier</h3>
              <p className="text-xs text-[#B59ED1] mb-4">Ces informations m&apos;aident à établir un devis précis (déplacement, surface, type de lieu).</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-[#B59ED1]">Adresse</Label>
                  <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="N° et nom de rue" className="mt-1 bg-[#1B132B] border-white/10" data-testid="quote-address" />
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-[#B59ED1]">Code postal</Label>
                    <Input value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} placeholder="26200" className="mt-1 bg-[#1B132B] border-white/10" data-testid="quote-zip" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-[#B59ED1]">Ville</Label>
                    <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Montélimar" className="mt-1 bg-[#1B132B] border-white/10" data-testid="quote-city" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#B59ED1]">Surface approximative (m²)</Label>
                  <Input type="number" value={form.surface} onChange={e => setForm({...form, surface: e.target.value})} placeholder="ex : 80" className="mt-1 bg-[#1B132B] border-white/10" data-testid="quote-surface" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Message */}
          <Card className="bg-[#261B3B] border-white/10">
            <CardContent className="pt-5">
              <Label className="text-xs text-[#B59ED1] mb-2 block">Décris ta demande *</Label>
              <Textarea
                value={form.message}
                onChange={e => setForm({...form, message: e.target.value})}
                placeholder="Pourquoi souhaites-tu une purification ? Ressentis particuliers, événements vécus dans le lieu, type de bien (maison, appartement, local commercial, terrain), accès..."
                className="bg-[#1B132B] border-white/10 min-h-[160px]"
                data-testid="quote-message"
              />
              <p className="text-[10px] text-[#B59ED1] mt-2">Plus tu donnes de détails, plus mon devis sera précis.</p>
            </CardContent>
          </Card>

          <div className="p-4 bg-[#00E5FF]/5 border border-[#00E5FF]/20 rounded-lg flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" />
            <div className="text-xs text-[#E2D5F8] leading-relaxed">
              Aucun paiement ne te sera demandé à cette étape. Je reviens vers toi sous <strong className="text-white">48h ouvrées</strong> avec un devis détaillé. Tu pourras alors choisir de l&apos;accepter ou non.
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={loading || !selectedService} className="w-full gradient-primary h-12 text-base" data-testid="quote-submit">
            {loading ? 'Envoi...' : 'Envoyer ma demande de devis'}
          </Button>
        </div>
      </div>
    </main>
  );
}

// ============== APP ROUTER ==============

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Exception : si la boutique a une position de scroll sauvegardée (back-nav depuis fiche produit),
    // on laisse BoutiquePage gérer la restauration au lieu de forcer un scroll-top.
    if (pathname === "/boutique") {
      try {
        const saved = sessionStorage.getItem("boutique_nav_scroll_v1");
        if (saved && parseInt(saved, 10) > 100) return;
      } catch { /* ignore */ }
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL hash - handle BEFORE routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/evjf" element={<EvjfPage />} />
        <Route path="/services/soiree-privee" element={<SoireePriveePage />} />
        <Route path="/voyance-a-distance" element={<VoyanceDistancePage />} />
        {/* Local SEO landing pages (Montélimar) */}
        <Route path="/voyance-montelimar" element={<VoyanceMontelimarPage />} />
        <Route path="/pranic-healing-montelimar" element={<PranicHealingMontelimarPage />} />
        <Route path="/numerologie-montelimar" element={<NumerologieMontelimarPage />} />
        <Route path="/qui-suis-je" element={<QuiSuisJePage />} />
        <Route path="/a-propos" element={<QuiSuisJePage />} />
        <Route path="/medium-montelimar" element={<QuiSuisJePage />} />
        <Route path="/mentions-legales" element={<LegalNoticePage />} />
        <Route path="/politique-de-confidentialite" element={<PrivacyPolicyPage />} />
        <Route path="/cgv" element={<CguPage />} />
        <Route path="/politique-consultations" element={<ConsultationPolicyPage />} />
        <Route path="/evenements" element={<EventsPage />} />
        <Route path="/evenements/:slug" element={<EventsPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
        {/* Legacy Wix redirects: /post/<slug> -> /blog/<slug> (301-like via replace) */}
        <Route path="/post/:slug" element={<LegacyPostRedirect />} />
        {/* Legacy Wix redirects: /service-page/<slug> -> /services or /evenements */}
        <Route path="/service-page/:slug" element={<LegacyServicePageRedirect />} />
        {/* Legacy Wix redirects: /event-details/<slug> -> /evenements */}
        <Route path="/event-details/:slug" element={<LegacyEventDetailsRedirect />} />
        <Route path="/event-details/:slug/*" element={<LegacyEventDetailsRedirect />} />
        {/* Legacy Wix static redirects (1:1 mapping) */}
        <Route path="/event-list" element={<LegacyStaticRedirect to="/evenements" />} />
        <Route path="/book-online" element={<LegacyStaticRedirect to="/booking" />} />
        <Route path="/contact" element={<LegacyStaticRedirect to="/qui-suis-je" />} />
        <Route path="/voyance-guidances-a-montelimar" element={<LegacyStaticRedirect to="/voyance-montelimar" />} />
        <Route path="/pranic-healing-a-montelimar" element={<LegacyStaticRedirect to="/pranic-healing-montelimar" />} />
        <Route path="/bilan-numerologique-a-montelimar" element={<LegacyStaticRedirect to="/numerologie-montelimar" />} />
        <Route path="/purifications-lieux-et-personnes-a-montelimar" element={<LegacyStaticRedirect to="/services" />} />
        <Route path="/boutique-spirituelle-montelimar" element={<LegacyStaticRedirect to="/boutique" />} />
        <Route path="/quelle-prestation-choisir" element={<LegacyStaticRedirect to="/services" />} />
        <Route path="/quelle-prestation-choisir-1" element={<LegacyStaticRedirect to="/services" />} />
        <Route path="/services-8" element={<LegacyStaticRedirect to="/services" />} />
        <Route path="/copie-de-purification-personne" element={<LegacyStaticRedirect to="/services" />} />
        <Route path="/séjours-spirituels" element={<LegacyStaticRedirect to="/retraites" />} />
        <Route path="/sejours-spirituels" element={<LegacyStaticRedirect to="/retraites" />} />
        <Route path="/payment-request-page" element={<LegacyStaticRedirect to="/" />} />
        <Route path="/boutique" element={<BoutiquePage />} />
        <Route path="/boutique/:productId" element={<ProductDetailPage useCart={useCart} />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/devis" element={<QuotePage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/pay-appointment/:appointment_id" element={<PayAppointmentByLink />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/retraites" element={<RetreatsPage />} />
        <Route path="/retraites/reservation/succes" element={<RetreatBookingSuccessPage />} />
        <Route path="/retraites/:slug" element={<RetreatDetailPage />} />
        <Route path="/retraite-spirituelle-drome-provence" element={<RetreatLandingDromeProvence />} />
        <Route path="/retraite-spirituelle-sud-france" element={<RetreatLandingSudFrance />} />
        {/* Auth email/password */}
        <Route path="/connexion" element={<LoginPage />} />
        <Route path="/inscription" element={<RegisterPage />} />
        <Route path="/verifier-email/:token" element={<VerifyEmailPage />} />
        <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
        <Route path="/reinitialiser-mot-de-passe/:token" element={<ResetPasswordPage />} />
        <Route path="/confirmer-email/:token" element={<ConfirmEmailChangePage />} />
      </Routes>
      <Footer />
      {/* Modal globale Auth Choice (Google ou Email/MDP) */}
      <GlobalAuthModal />
    </>
  );
}

// Modal globale de choix de méthode de connexion
function GlobalAuthModal() {
  const { authModalOpen, setAuthModalOpen, googleLogin } = useAuth();
  return (
    <AuthChoiceModal
      open={authModalOpen}
      onOpenChange={setAuthModalOpen}
      googleLogin={googleLogin}
    />
  );
}

// ============== MAIN APP ==============

function App() {
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Data already seeded or error
      }
    };
    seedData();
    // Register Service Worker (PWA + Push)
    registerServiceWorker();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                background: '#261B3B',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff'
              }
            }}
          />
          <AppRouter />
          <NoIndexBanner />
          <PWAInstallPrompt />
          <CookieBanner />
          <MobileBookingCTA />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
